/*
 * P2 · 核心工作页（左预览 + 右对话）
 *
 * 布局：
 *   ┌─────────────────────────────────────┐
 *   │  顶部：文档名                        │
 *   │  ┌──────────────┐ ┌──────────────┐ │
 *   │  │ 左：预览     │ │ 右：对话     │ │
 *   │  │ DocumentPreview │ ChatPanel  │ │
 *   │  └──────────────┘ └──────────────┘ │
 *   └─────────────────────────────────────┘
 *
 * 数据流：
 *   1. URL 拿到上传后的 fileUrl
 *   2. 左侧预览 fileUrl（AI 推送新版本时切换）
 *   3. 用户在预览选中 → 弹菜单 → 「加入到会话」 → quotes 累积
 *   4. 用户输入 + 发送 → SSE 流式回复
 *   5. AI 通过 markdown + attachments 推送内容（含 10 种代码块 + 交互附件）
 *   6. AI 推送 preview-update → 自动切换左侧
 *   7. 用户点附件按钮 → 转化为新一轮对话发给 AI
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ReactDOM from 'react-dom'
import { Input, Button, Popover, Tooltip, Select, message } from 'antd'
import {
  BookFilled,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileExcelFilled,
  FileFilled,
  FilePdfFilled,
  FilePptFilled,
  FileTextFilled,
  FileWordFilled,
  FolderFilled,
  LeftOutlined,
  LoadingOutlined,
  PictureFilled,
  PictureOutlined,
  PlusOutlined,
  RedoOutlined,
  RightOutlined,
  ShareAltOutlined,
  SyncOutlined,
  ThunderboltFilled
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  DocumentPreview,
  SelectedContent,
  getRenderedCellTextForEditModal
} from '../components/DocumentPreview'
import {
  ChatPanel,
  QuoteItem,
  buildMessageWithQuotes
} from '../components/ChatPanel'
import { useSSEChat } from '../hooks/useSSEChat'
import { pickUserAskDisplayAs } from '../utils/userAskDisplayAs'
import { parseEditTools } from '../utils/editTools'
import { xheyAi, SINGLE_QUERY_COST } from '../api'
import { useCredits } from '../ctx/CreditsCtx'
import { useCurrentFile } from '../ctx/FileCtx'
import type { AttachmentAction } from '../components/attachments'
import { ShareTargets } from '../components/attachments/ShareTargets'
import { TeamPhotoPickerModal } from '../components/TeamPhotoPickerModal'
import type { TeamPhotoPickResult } from '../components/TeamPhotoPickerModal'
import UserAskActionSheet from '../components/UserAskActionSheet'
import {
  usePayment,
  useVipKind,
  useAppSelector,
  useAgentCustomerDrawer
} from '../../../app/hooks'
import PointsPackModal from '../../components/PointsPackModal'
import CreditsExhaustedModal from '../components/CreditsExhaustedModal'
import { aiClerkPayContent } from '../utils/paymentContent'
import PointsEntryChip from '../../components/PointsEntryChip'
import PointsStatusCard from '../../components/PointsStatusCard'
import CONFIG from '../../../constants/config'
import VipCrownIcon from './images/icon-vip-crown.svg?react'
import type { ChatMessage, MessagePart } from '../types'
import {
  trackEnter,
  trackClick,
  trackDialogShow,
  trackDialogClick,
  trackResult,
  readSessionId,
  mapCreditUsageState
} from '../utils/track'
import {
  computeUsageState,
  vipKindToThemeKey
} from '../../components/PointsStatusCard/config'
import { pickAiFillSelectionKind } from '../utils/selectionKind'
import './index.scss'

const CONV_KEY = 'aiClerk:conversationId'

// 文件类型 → antd6 实心图标查表（pickPreviewFileIcon 返回的 type 字符串）
const FILE_FILLED_ICONS: Record<string, React.ComponentType<any>> = {
  'file-excel': FileExcelFilled,
  'file-word': FileWordFilled,
  'file-ppt': FilePptFilled,
  'file-pdf': FilePdfFilled,
  'file-text': FileTextFilled,
  file: FileFilled
}
const FileTypeIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = FILE_FILLED_ICONS[name]
  return C ? <C {...rest} /> : null
}

// 文件名 → 顶部预览条 antd Icon 类型 + 主色;支持 Word / Excel / PowerPoint / PDF
function pickPreviewFileIcon(filename: string): {
  type:
    | 'file-excel'
    | 'file-word'
    | 'file-ppt'
    | 'file-pdf'
    | 'file-text'
    | 'file'
  color: string
} {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { type: 'file-excel', color: '#1B7C3E' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { type: 'file-word', color: '#1565C0' }
  }
  if (ext === 'pptx' || ext === 'ppt') {
    return { type: 'file-ppt', color: '#D84315' }
  }
  if (ext === 'pdf') {
    return { type: 'file-pdf', color: '#C62828' }
  }
  if (ext === 'txt' || ext === 'md') {
    return { type: 'file-text', color: '#616161' }
  }
  return { type: 'file', color: '#757575' }
}

// ───────────── 文档操作状态机（导出/保存模板/分享 三件套用）─────────────
//
// 五态 (DocActionPhase)：
//   idle        — 文档已挂载但 AI 还没动过
//   editing     — AI 正在写入 OR 最近 edit 完成不到 1.5s（防抖避免按钮闪烁）
//   ready       — 当前版本就绪，可导出/保存/分享
//   staleReady  — 用户已经导出/保存/分享过，但 AI 又改了 → 提醒"已有新版本"
//   error       — 最近一次 edit tool 报错
//
// 触发依据：扫所有 assistant 消息里的 excel_edit / word_edit tool-call，
//   - state ∈ {input-streaming, input-available} 或所在 message streaming → editing
//   - state === 'output-error' → error
//   - state === 'output-available' → 看 endedAt 距今多久，<1.5s 仍 editing；
//     ≥1.5s 且 lastConsumedAt > 0 且 endedAt > lastConsumedAt → staleReady；否则 ready
//
// lastPreviewSyncAtRef: 用户点过"刷新预览"按钮就更新,配合 endedAt 比对识别 staleReady。
//   (旧设计是跟三件套挂钩,但用户反馈"已经刷新过 badge 还显示"语义混乱,改成
//    只跟刷新预览挂钩 — badge 现在表达的是"预览版本 vs AI 最新版本"是否同步,
//    跟外部导出副本无关。)
const READY_DEBOUNCE_MS = 1500

type DocActionPhase = 'idle' | 'editing' | 'ready' | 'staleReady' | 'error'

interface DocActionState {
  phase: DocActionPhase
  /** AI 编辑中时的提示语（如有 args.range / args.cell_range） */
  editingHint?: string
  /** 最近一次 edit 完成时间戳（ready/staleReady 用） */
  latestEditEndedAt?: number
  /** error 时的报错文本 */
  errorMessage?: string
}

const { TextArea } = Input

interface QuickEditImageAttachment {
  fileName: string
  fileUrl?: string
  mediaType: string
  previewUrl?: string
  status: 'uploading' | 'done' | 'error'
  error?: string
}

function buildPreviewUrl(url: string, nonce: number): string {
  if (!url) return ''
  if (!nonce) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}t=${nonce}`
}

function toAbsoluteUrl(url: string): string {
  if (!url) return ''
  try {
    return new URL(url, window.location.origin).toString()
  } catch {
    return url
  }
}

function stripPreviewNonce(url: string): string {
  if (!url) return ''
  return url.replace(/([?&])t=\d+(&)?/, (_, sep: string, tail: string) =>
    tail ? sep : ''
  )
}

function isImageCellContent(content: SelectedContent | null): boolean {
  if (!content) return false
  // 浮窗的"添加图片/换图/添加文字/修改"会显式带 quickEditMode hint:
  // 此时按 hint 决定 modal 形态(空 cell 也能"添加图片",不需要 cell 自身有 image)
  if (content.quickEditMode === 'image') return true
  if (content.quickEditMode === 'image-batch') return true
  if (content.quickEditMode === 'text') return false
  const firstCell = content.rangePayload?.cells[0]?.[0]
  if (!firstCell) return false
  return (
    firstCell.type === 'image' ||
    !!firstCell.imageSrc ||
    firstCell.displayText === '[图片]'
  )
}

/** 批量图片模式 hint:浮窗"添加图片/换图"在多 cell 选区下触发,modal 渲染 N 格 grid */
function isImageBatchContent(content: SelectedContent | null): boolean {
  return !!content && content.quickEditMode === 'image-batch'
}

/** 选区内有效 cell 数(跳过合并占位的 ref);批量模式下决定 grid 格数 + 上传上限 */
function effectiveCellRefsFromContent(
  content: SelectedContent | null
): string[] {
  if (!content?.rangePayload) return []
  const refs: string[] = []
  for (const row of content.rangePayload.cells) {
    for (const c of row) {
      if (c.isMergedPart) continue
      refs.push(c.ref)
    }
  }
  return refs
}

/**
 * 后端历史消息 → 前端 ChatMessage（对齐 XHeyClaw-Web）
 *
 * 后端 /api/sessions/{id}/history 每条 message 实际上是 LLM 一个执行步：
 *   { role: 'user' | 'assistant' | 'toolResult',
 *     content: [ {type:'text'|'thinking'|'toolCall', ...} ],
 *     timestamp }
 *
 * **关键**：同一轮对话里 assistant + toolResult 会重复多次（思考→工具→结果→
 * 思考→工具→结果→…→最终文本），全部属于"对同一个 user 问题的一次回答"。
 * 直接 1:1 渲染会爆出 N 个独立气泡（带 N 个头像 + 时间戳，看着支离破碎）。
 *
 * 对齐 XHeyClaw-Web assistant-ui 的渲染语义：把每一段 user 之后的所有
 * assistant + toolResult 合并成 **一个** ChatMessage，parts 按出现顺序串起来。
 * toolResult 不生成独立 part，按位置匹配到上一条 assistant 的 toolCall part
 * 上，作为该 part 的 output。
 *
 * 兼容：旧 OpenAI 格式（content 是 string + tool_calls 字段 + role='tool'）也支持。
 */
function hydrateHistory(raw: any[]): ChatMessage[] {
  const toMs = (v: unknown): number => {
    if (typeof v === 'number') return v
    if (typeof v === 'string') {
      const t = new Date(v).getTime()
      return Number.isNaN(t) ? Date.now() : t
    }
    return Date.now()
  }

  const normalizeHistoryPart = (
    part: any,
    startedAt: number
  ): MessagePart | null => {
    if (!part || typeof part !== 'object') return null

    const toUserAskPart = (
      payload: any,
      fallbackPendingId?: string
    ): MessagePart => ({
      type: 'user-ask',
      pendingId:
        payload.pendingId || payload.pending_id || fallbackPendingId || '',
      title: payload.title || '',
      description: payload.description,
      questions: Array.isArray(payload.questions) ? payload.questions : [],
      autoFillPreview: Array.isArray(payload.autoFillPreview)
        ? payload.autoFillPreview
        : Array.isArray(payload.auto_fill_preview)
          ? payload.auto_fill_preview
          : undefined,
      submitted: payload.submitted === true,
      // 历史 user_ask 跟 SSE 实时路径用同一份 helper 分流:
      //   - title 命中"保存模板"等关键词 → 'inline' chat 流原位 UserAskBlock
      //   - 其他 → 'chip' + 点击重开 ActionSheet
      displayAs: pickUserAskDisplayAs(payload.questions, payload.title)
    })

    if (part.type === 'text' && typeof part.text === 'string') {
      return { type: 'text', text: part.text }
    }

    if (
      part.type === 'file' &&
      typeof part.url === 'string' &&
      typeof part.mediaType === 'string'
    ) {
      return {
        type: 'file',
        url: part.url,
        mediaType: part.mediaType,
        name:
          (typeof part.name === 'string' && part.name) ||
          part.url.split('/').pop() ||
          'file'
      }
    }

    if (
      (part.type === 'reasoning' || part.type === 'thinking') &&
      typeof (part.text || part.thinking) === 'string'
    ) {
      return {
        type: 'reasoning',
        text: part.text || part.thinking,
        state: part.state
      }
    }

    if (part.type === 'tool-call' || part.type === 'toolCall') {
      const rawArgs = part.args ?? part.arguments
      const toolName = part.toolName || part.name || 'tool'
      let parsedArgs: Record<string, unknown> | undefined
      if (rawArgs && typeof rawArgs === 'object') {
        parsedArgs = rawArgs as Record<string, unknown>
      } else if (typeof rawArgs === 'string') {
        try {
          parsedArgs = JSON.parse(rawArgs) as Record<string, unknown>
        } catch {
          /* 忽略 */
        }
      }

      if (toolName === 'user_ask' || toolName === 'user-ask') {
        return toUserAskPart(
          parsedArgs || {},
          String(part.toolCallId || part.id || '')
        )
      }

      return {
        type: 'tool-call',
        toolCallId: String(part.toolCallId || part.id || ''),
        toolName,
        args: parsedArgs,
        argsText: typeof part.argsText === 'string' ? part.argsText : undefined,
        state: part.state || 'input-available',
        output: part.output,
        errorText:
          typeof part.errorText === 'string' ? part.errorText : undefined,
        cancelReason:
          typeof part.cancelReason === 'string' ? part.cancelReason : undefined,
        startedAt:
          typeof part.startedAt === 'number' ? part.startedAt : startedAt,
        endedAt: typeof part.endedAt === 'number' ? part.endedAt : undefined
      }
    }

    if (part.type === 'user-ask' || part.type === 'user_ask') {
      return toUserAskPart(part, String(part.id || ''))
    }

    return null
  }

  // 旧 OpenAI 兼容：role='tool' + tool_call_id 时按 id 索引
  const toolOutputById = new Map<
    string,
    { output: unknown; isError: boolean }
  >()
  for (const m of raw) {
    if (m && m.role === 'tool' && m.tool_call_id) {
      let output: unknown = m.content
      if (typeof m.content === 'string') {
        try {
          output = JSON.parse(m.content)
        } catch {
          /* 忽略 */
        }
      }
      toolOutputById.set(String(m.tool_call_id), {
        output,
        isError: m.is_error === true || m.status === 'error'
      })
    }
  }

  /** 把单条后端 message 的 content 数组转成 part 列表（不处理 toolResult） */
  const partsFromContent = (
    m: any,
    startedAt: number
  ): { parts: MessagePart[]; toolCallIndices: number[] } => {
    const parts: MessagePart[] = []
    const toolCallIndices: number[] = []

    if (Array.isArray(m.parts) && m.parts.length > 0) {
      for (const rawPart of m.parts) {
        const normalized = normalizeHistoryPart(rawPart, startedAt)
        if (!normalized) continue
        parts.push(normalized)
      }
      parts.forEach((p, idx) => {
        if (p.type === 'tool-call') toolCallIndices.push(idx)
      })
      return { parts, toolCallIndices }
    }

    if (Array.isArray(m.content)) {
      for (const c of m.content) {
        const normalized = normalizeHistoryPart(c, startedAt)
        if (!normalized) continue
        if (normalized.type === 'text' && normalized.text === '') continue
        if (normalized.type === 'reasoning' && !normalized.state) {
          normalized.state = 'done'
        }
        parts.push(normalized)
        if (normalized.type === 'tool-call') {
          toolCallIndices.push(parts.length - 1)
        }
      }
      return { parts, toolCallIndices }
    }

    if (typeof m.content === 'string' && m.content !== '') {
      parts.push({ type: 'text', text: m.content })
      if (m.role === 'assistant' && Array.isArray(m.tool_calls)) {
        for (const tc of m.tool_calls) {
          const callId = String(tc.id || '')
          if (!callId) continue
          const fn = tc.function || {}
          let parsedArgs: Record<string, unknown> | undefined
          if (typeof fn.arguments === 'string') {
            try {
              parsedArgs = JSON.parse(fn.arguments)
            } catch {
              /* 忽略 */
            }
          } else if (fn.arguments && typeof fn.arguments === 'object') {
            parsedArgs = fn.arguments as Record<string, unknown>
          }
          const matched = toolOutputById.get(callId)
          parts.push({
            type: 'tool-call',
            toolCallId: callId,
            toolName: fn.name || 'tool',
            args: parsedArgs,
            state: matched
              ? matched.isError
                ? 'output-error'
                : 'output-available'
              : 'input-available',
            output: matched ? matched.output : undefined,
            errorText:
              matched && matched.isError
                ? typeof matched.output === 'string'
                  ? matched.output
                  : JSON.stringify(matched.output)
                : undefined
          })
          toolCallIndices.push(parts.length - 1)
        }
      }
    }
    return { parts, toolCallIndices }
  }

  const visible: ChatMessage[] = []
  // 当前正在累积的 assistant ChatMessage 引用 + 它内部待配对的 toolCall part 索引
  let curAssistant: ChatMessage | null = null
  let curPendingTools: number[] = []

  const flushAssistant = () => {
    if (curAssistant && curAssistant.parts.length > 0) {
      visible.push(curAssistant)
    }
    curAssistant = null
    curPendingTools = []
  }

  for (let i = 0; i < raw.length; i++) {
    const m = raw[i] || {}
    const ts = toMs(m.timestamp ?? m.created_at ?? m.createdAt)

    if (m.role === 'user') {
      flushAssistant()
      const { parts } = partsFromContent(m, ts)
      if (parts.length === 0) continue
      // 发送侧 api.ts chatStream / 后端持久化时会把附件以多种格式注入到
      // 用户消息文本，历史回显场景：
      //   - 先**解析**出附件 URL，合成 file parts 给气泡渲染缩略图 / 文件卡
      //   - 再**剥掉**注入段，让 text 回到用户原始输入
      //   注入格式：
      //     1) 旧 markdown 链接：`\n\n[filename](url)`
      //     2) 当前消息附带的图片：![](url) / 文件：[name](url)
      //     3) 后端给 LLM 加的 `[附件: xxx]\n可用 \`python ... download ...\` 下载到本地后操作。`
      const harvestedFiles: Array<{
        url: string
        name: string
        mediaType: string
      }> = []
      const seenUrls = new Set<string>()
      const addFile = (url: string, name: string, isImage: boolean) => {
        if (!url || seenUrls.has(url)) return
        seenUrls.add(url)
        const ext = (name.split('.').pop() || '').toLowerCase()
        const mediaType = isImage
          ? `image/${ext === 'jpg' ? 'jpeg' : ext || 'png'}`
          : ext === 'pdf'
            ? 'application/pdf'
            : ext === 'xlsx'
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : ext === 'xls'
                ? 'application/vnd.ms-excel'
                : ext === 'docx'
                  ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  : ext === 'doc'
                    ? 'application/msword'
                    : 'application/octet-stream'
        harvestedFiles.push({ url, name, mediaType })
      }

      const cleanedParts: MessagePart[] = parts.map((p) => {
        if (p.type !== 'text') return p

        // 解析 <message-attachments> ... </message-attachments> 块（composer 上传的附件，要展示）。
        // 块内每行支持 ![name](url) 图片 或 [name](url) 文件。
        // <current-doc> 块**不解析**（主文档左侧已预览，不重复展示）。
        const blockRe =
          /<message-attachments>\s*([\s\S]+?)\s*<\/message-attachments>/g
        let bm: RegExpExecArray | null
        while ((bm = blockRe.exec(p.text)) !== null) {
          const body = bm[1]
          const lineRe = /(!?)\[([^\]]*)\]\(([^)]+)\)/g
          let lm: RegExpExecArray | null
          while ((lm = lineRe.exec(body)) !== null) {
            const isImage = lm[1] === '!'
            const name =
              lm[2] || lm[3].split('/').pop() || (isImage ? 'image' : 'file')
            addFile(lm[3], name, isImage)
          }
        }

        // 剥掉两段标签 + 后端给 LLM 加的下载引导段
        const stripped = p.text
          .replace(
            /\n*<message-attachments>[\s\S]+?<\/message-attachments>/g,
            ''
          )
          .replace(/\n*<current-doc>[\s\S]+?<\/current-doc>/g, '')
          .replace(/\n*<aiclerk-scene>[\s\S]*?<\/aiclerk-scene>/g, '')
          .replace(
            /\n*<aiclerk-blank-guide>[\s\S]*?<\/aiclerk-blank-guide>/g,
            ''
          )
          .replace(
            /\n*\[附件:\s*[^\]]+\][^`]*`[^`]+`[^\n]*?下载到本地后操作。/g,
            ''
          )
        return { ...p, text: stripped.replace(/\s+$/, '') }
      })

      // 把解析出的附件作为 file parts 追加，让用户气泡能渲染缩略图 / 文件卡
      for (const f of harvestedFiles) {
        cleanedParts.push({
          type: 'file',
          url: f.url,
          mediaType: f.mediaType,
          name: f.name
        })
      }

      visible.push({
        id: m.id || `h-u-${i}`,
        role: 'user',
        parts: cleanedParts,
        createdAt: ts
      })
      continue
    }

    if (m.role === 'assistant') {
      const { parts: newParts, toolCallIndices } = partsFromContent(m, ts)
      if (newParts.length === 0) continue
      if (!curAssistant) {
        curAssistant = {
          id: m.id || `h-a-${i}`,
          role: 'assistant',
          parts: [],
          createdAt: ts
        }
        curPendingTools = []
      }
      const baseIdx = curAssistant.parts.length
      curAssistant.parts.push(...newParts)
      // 维护待配对队列：本条 assistant 新增的 toolCall part 加进去
      curPendingTools.push(...toolCallIndices.map((idx) => idx + baseIdx))
      continue
    }

    if (m.role === 'toolResult') {
      // 把 output 灌到最近 assistant 的待配对 toolCall part 上
      if (!curAssistant) continue
      const partIdx = curPendingTools.shift()
      if (partIdx == null) continue
      const target = curAssistant.parts[partIdx]
      if (!target || target.type !== 'tool-call') continue
      let outText = ''
      if (Array.isArray(m.content)) {
        for (const c of m.content) {
          if (c && c.type === 'text' && typeof c.text === 'string') {
            outText = c.text
            break
          }
        }
      } else if (typeof m.content === 'string') {
        outText = m.content
      }
      let parsed: unknown = outText
      let isError = false
      try {
        const o = JSON.parse(outText)
        parsed = o
        if (o && (o.status === 'error' || o.is_error === true)) isError = true
      } catch {
        /* 忽略 */
      }
      target.output = parsed
      target.state = isError ? 'output-error' : 'output-available'
      target.endedAt = ts
      if (isError) {
        target.errorText =
          typeof parsed === 'string'
            ? parsed
            : ((parsed && (parsed as any).error) as string) ||
              JSON.stringify(parsed)
      }
      continue
    }

    // 其他 role（如 'tool' / 'system'）忽略
  }
  flushAssistant()
  return visible
}

const Work: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  // 团队切换：groupId 变化 → 清当前会话 + 回退 AI 填表员首页。
  // 旧 sessionId 属于旧团队，配新 groupId 继续请求 history / chat 会 404。
  const groupId = useAppSelector((state: any) => state.group?.groupId || '')
  const initialGroupIdRef = useRef<string>('')
  useEffect(() => {
    if (!groupId) return
    // 记录首个非空 groupId 作为基准（首次进页面不回退）
    if (!initialGroupIdRef.current) {
      initialGroupIdRef.current = groupId
      return
    }
    if (groupId !== initialGroupIdRef.current) {
      try {
        window.localStorage.removeItem(CONV_KEY)
      } catch {
        /* localStorage 不可用 */
      }
      navigate('/ai/clerk')
    }
  }, [groupId, navigate])

  const { file } = useCurrentFile()
  // Platform Shared Instance：sessionId 同时作为 X-Conversation-Id；
  // 旧链接里的 conversationId 仍兼容，作为 fallback
  const resumeSessionId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('sessionId') || params.get('conversationId') || ''
  }, [location.search])

  // 积分余额提前到 trackEnter useEffect 之前，让 enter 能等到 credits ready 再上报
  // number1 + string2（积分增量埋点要求）。原本在下方，提到这里：hook 顺序与原代码
  // 兼容(useState 在它之后，先调 useCredits 也合法)，creditsRef 给 effect 闭包用。
  const { credits, refresh: refreshCredits } = useCredits()
  const creditsRef = useRef(credits)
  creditsRef.current = credits

  // KV `AI_form_filling_edit_tools` 配的"文档编辑工具"列表(默认 excel_edit/word_edit)。
  // 命中这些工具名 → 触发预览刷新 / footer 挂载 / 编辑态 / ai_fill 埋点。
  // config 是 app 启动异步拉的:未到时先用默认值,到了 useMemo 重算自动纠正。
  const aiClerkConfig = useAppSelector((state: any) => state.main.config)
  const editToolSet = useMemo(
    () =>
      new Set(
        parseEditTools(
          aiClerkConfig && aiClerkConfig['AI_form_filling_edit_tools']
        )
      ),
    [aiClerkConfig]
  )
  // onDone 等"refs 模式"回调里用最新 set(同 creditsRef 套路)
  const editToolSetRef = useRef(editToolSet)
  editToolSetRef.current = editToolSet

  // 当前团队 (= group.invite_id, 跟 api.ts:getTeamNo 同口径).
  // 用 useAppSelector 是为了**响应式**捕捉切团队动作 — getTeamNo() 是 store.getState() 一次性读,
  // 不会随 dispatch 触发 re-render. 切团队后 abort 旧 run 的 effect 必须看实时 teamNo.
  const teamNo = useAppSelector((s: any) => s?.group?.invite_id || '')

  // 进入 AI 填写交互页埋点
  //
  // sessionId 走 conversationId,生成时机:
  //   - resume 场景: URL 带 sessionId/conversationId 参数 → mount 就有
  //   - 新会话场景: home 跳转前会 removeItem(CONV_KEY) 强制开新会话,然后由
  //     useSSEChat 首次 send 前 getOrCreateConversationId() 生成并写入 localStorage
  //     这中间有几百 ms ~ 几秒延迟(等用户首条 prompt + SSE 启动)
  //
  // 因此 mount 立即上报会拿到空 sessionID. 改成:
  //   1. mount 立即读一次,有就立即上报
  //   2. 没有就 polling localStorage,首次出现立刻上报
  //   3. 60s 兜底超时(防 SSE 卡住永远不触发),仍上报但 sessionID 留空
  useEffect(() => {
    let reported = false
    let intervalId: number | null = null
    let timeoutId: number | null = null
    const report = (sid: string) => {
      if (reported) return
      reported = true
      // 积分增量埋点：进入页面携带剩余积分(number1)+ 使用状态(string2)。
      // credits 可能晚于 sid 到位(异步拉余额)；超时 60s 仍发，没拿到就传 0/unused 兜底。
      const cur = creditsRef.current
      let number1: number | undefined
      let string2: string | undefined
      if (cur && cur.total_quota != null && cur.consumed != null) {
        const total = cur.total_quota || 0
        const used = cur.consumed || 0
        number1 = Math.max(0, total - used)
        string2 = mapCreditUsageState(
          computeUsageState(total, used, SINGLE_QUERY_COST)
        )
      }
      trackEnter('formFilling', {
        requirement_id: 1103,
        track_sign: 'aai.b8137.pageview.ai_enter_web_ai_form_filling',
        sessionID: sid,
        number1,
        string2
      })
      if (intervalId !== null) window.clearInterval(intervalId)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
    // 同时等 sessionID + credits ready；二者齐全(或超时)即上报。
    const trySid = () => resumeSessionId || readSessionId()
    const tryReport = () => {
      const sid = trySid()
      if (sid && creditsRef.current) report(sid)
    }
    const initialSid = trySid()
    if (initialSid && creditsRef.current) {
      report(initialSid)
      return
    }
    intervalId = window.setInterval(tryReport, 500)
    // 60s 兜底：sid 优先(原约定)，credits 拿不到就空字段上报，整页埋点不能因此卡死
    timeoutId = window.setTimeout(() => report(trySid() || ''), 60_000)
    return () => {
      if (intervalId !== null) window.clearInterval(intervalId)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // 「继续使用」入口带的技能信息:
  //   skillTitle = 中文 display_name (LLM 对话语义友好)
  //   skillName  = ASCII slug (`[A-Za-z0-9_-]`), 调 skill 工具的 name 参数必须用这个,
  //                Server NAMING 校验拒中文. 不传 LLM 会拿 title 当 name 直接报错.
  const skillTitle = useMemo(() => {
    return new URLSearchParams(location.search).get('skillTitle') || ''
  }, [location.search])
  const skillName = useMemo(() => {
    return new URLSearchParams(location.search).get('skill') || ''
  }, [location.search])
  const fileUrl = file?.fileUrl || ''
  const fileName = file?.fileName || '未命名文档'
  // 左侧预览取 refreshUrl（上传接口专门给前端预览的 URL）；老数据兜底 fileUrl
  const previewBaseUrl = file?.refreshUrl || fileUrl
  // docx 预览的 zoom (CSS zoom) 在 docx-preview 渲染产物上视觉上无效
  // (zoom 改了盒子但 docx 用绝对单位定的页宽,缩放后内容不重排,看不出区别),
  // 所以这种文件不展示 zoom 控件。xlsx 则按比例缩放,有效。
  const isDocxPreview = useMemo(() => {
    return /\.docx$/i.test(fileName) || /\.doc$/i.test(fileName)
  }, [fileName])

  // 当前预览 URL（AI 可能推送新版本：标记后的、填充后的、最终成品）
  const [currentPreviewUrl, setCurrentPreviewUrl] =
    useState<string>(previewBaseUrl)
  // 预览缩放:4 档(50% / 70% / 100% / 115%)。默认 70%——许多 docx 用 A4 横向
  // 表格(多列照片),100% 会超出左侧面板把右侧裁掉,初始 70% 一般能完整放进
  // 面板;用户想要更大可选 100% / 115%。
  const [previewZoom, setPreviewZoom] = useState<number>(0.7)
  const payment = usePayment()
  const vipKind = useVipKind()
  // 导出 / 分享需「高级及以上」(高级 VVIP / 超级 SVIP / 企业 EVIP)；
  // 基础会员(初级 VIP)与非会员都卡点 → 引导升级到高级。
  // ⚠ vip_kind 数值不代表等级(VIP=1/SVIP=2/VVIP=3/EVIP=4)，必须按枚举显式列举。
  const canExportShare =
    vipKind === CONFIG.VIP_KIND.VVIP ||
    vipKind === CONFIG.VIP_KIND.SVIP ||
    vipKind === CONFIG.VIP_KIND.EVIP
  // 卡点 → 打开开通弹窗并默认选「高级」tab(导出/分享解锁的最低档)
  const openExportShareUpgrade = useCallback(() => {
    payment.openVipByKind(
      CONFIG.VIP_KIND.VVIP,
      'aiFormFiller',
      false,
      aiClerkPayContent({
        isVip: vipKind > 0,
        vipKind,
        usedPoints: credits?.consumed ?? 0
      })
    )
  }, [payment, vipKind, credits])
  const agentCustomerDrawer = useAgentCustomerDrawer()
  const [pointsPackVisible, setPointsPackVisible] = useState(false)
  const [creditsExhaustedVisible, setCreditsExhaustedVisible] = useState(false)
  // 积分入口 hover 时展示「我的积分」卡片
  const [pointsCardVisible, setPointsCardVisible] = useState(false)

  // 积分增量埋点：所有积分事件统一通过这个 helper 拿 number1(剩余) + string2(状态)，
  // 避免散落多处计算。credits 为 null(故障 / 还没拉到) → 两字段均不带。
  const buildCreditTrackingExtra = useCallback((): {
    number1?: number
    string2?: string
  } => {
    const cur = creditsRef.current
    if (!cur || cur.total_quota == null || cur.consumed == null) return {}
    const total = cur.total_quota || 0
    const used = cur.consumed || 0
    return {
      number1: Math.max(0, total - used),
      string2: mapCreditUsageState(
        computeUsageState(total, used, SINGLE_QUERY_COST)
      )
    }
  }, [])

  // 公共埋点 sign：所有积分相关 click / dialog 走同一 track_sign(b8137 业务行)
  const TRACK_SIGN_CLICK = 'aai.b8137.click.ai_click_web_ai_form_filling'
  const TRACK_SIGN_DIALOG_SHOW =
    'aai.b8137.pageview.ai_dialog_show_web_ai_form_filling'
  const TRACK_SIGN_DIALOG_CLICK =
    'aai.b8137.click.ai_dialog_click_web_ai_form_filling'

  // 积分 CTA：超级会员 → 增购积分包，其余 → 开通 / 升级会员
  const handlePointsCta = (ctx: {
    action: 'upgrade' | 'addon'
    used: number
    themeKey: 'none' | 'basic' | 'advanced' | 'super'
  }) => {
    // hover 积分卡 CTA：埋点按 PointsCardContext.action 分流
    // addon (svip) → credit_hover.buy_credits + 页面级 buy_credits 点击
    // upgrade (noVip/vip/vvip) → credit_hover.upgrade_member
    const extra = buildCreditTrackingExtra()
    if (ctx.action === 'addon') {
      // 同时上报「页面级 buy_credits 点击」(文档 ai_click.buy_credits)
      trackClick('formFilling', 'buy_credits', {
        requirement_id: 1103,
        track_sign: TRACK_SIGN_CLICK,
        sessionID: readSessionId(),
        ...extra
      })
      trackDialogClick('formFilling', 'credit_hover', 'buy_credits', {
        requirement_id: 1103,
        track_sign: TRACK_SIGN_DIALOG_CLICK,
        sessionID: readSessionId()
      })
      setPointsPackVisible(true)
      return
    }
    // 升级会员：打开比当前高一级的 tab（非会员→基础 / 基础→高级 / 高级→超级）
    trackDialogClick('formFilling', 'credit_hover', 'upgrade_member', {
      requirement_id: 1103,
      track_sign: TRACK_SIGN_DIALOG_CLICK,
      sessionID: readSessionId()
    })
    const nextKind =
      ctx.themeKey === 'basic'
        ? CONFIG.VIP_KIND.VVIP
        : ctx.themeKey === 'advanced'
          ? CONFIG.VIP_KIND.SVIP
          : CONFIG.VIP_KIND.VIP
    payment.openVipByKind(
      nextKind,
      'aiFormFiller',
      false,
      aiClerkPayContent({ isVip: vipKind > 0, vipKind, usedPoints: ctx.used })
    )
  }

  // 会员升级成功（vipKind 变化）→ 刷新积分余额（额度随等级变）
  const prevVipKindRef = useRef(vipKind)
  useEffect(() => {
    if (prevVipKindRef.current !== vipKind) {
      refreshCredits()
    }
    prevVipKindRef.current = vipKind
  }, [vipKind, refreshCredits])

  // 刷新计数：每次点"刷新"自增，把 t=<value> 拼到 refreshUrl 后面破缓存
  const [refreshNonce, setRefreshNonce] = useState<number>(0)
  // 用 docTick 在防抖窗口结束 / 刷新预览时强制 docActionState useMemo 重算
  // (避免 Date.now() 不变化时 hook 缓存命中)
  const [docTick, setDocTick] = useState(0)
  // 上次"预览同步到 AI 最新版本"的时间戳 — 用户点刷新预览时刷新,
  // 配合 edit tool 的 endedAt 比对识别 staleReady("AI 改了但预览还没刷新")
  const lastPreviewSyncAtRef = useRef<number>(0)
  const [previewLoadingState, setPreviewLoadingState] = useState<{
    busy: boolean
    phase: 'idle' | 'downloading' | 'rendering'
    progress: number | null
  }>({
    busy: false,
    phase: 'idle',
    progress: null
  })
  const previewSource = useMemo(() => {
    return buildPreviewUrl(currentPreviewUrl, refreshNonce)
  }, [currentPreviewUrl, refreshNonce])
  const triggerPreviewRefresh = useCallback(() => {
    setRefreshNonce((prev) => Math.max(Date.now(), prev + 1))
    // 刷新预览 == 用户主动把预览同步到 AI 最新版本 → 清掉 staleReady
    lastPreviewSyncAtRef.current = Date.now()
    setDocTick((x) => x + 1)
  }, [])
  // ref Set 记录已经触发过 refresh 的 toolCallId,避免同一个 edit tool 多次刷新.
  // 实际监听 messages 的 useEffect 必须等 useSSEChat 解构出 messages 之后才能挂.
  const refreshedToolCallsRef = useRef<Set<string>>(new Set())

  // 选中并「加入到会话」的引用
  const [quotes, setQuotes] = useState<QuoteItem[]>([])
  // quick action 预填给 ChatPanel 的输入框
  const [prefillInput, setPrefillInput] = useState<{
    text: string
    nonce: number
  } | null>(null)

  // 发送队列:SSE 正在进行(sending=true)时,用户新点的发送进队列等候,
  // SSE 完成后自动出队 1 条;等候期间用户可单条取消(从队列中删除)
  interface PendingMessage {
    id: string
    text: string // 用户原始文本(等候 chip 展示用)
    message: string // 实际下发的 finalMessage(已含 quote 上下文)
    attachments?: Array<{ url: string; name: string; mediaType: string }>
    /** quick action 触发(send-now 模式)时,带上 ai_fill 埋点的选中类型,
     *  用于 onDone 时上报 ai_fill result 的 string1 字段.普通用户 chat 留空. */
    aiFillSelectionKind?: string
  }
  const [pendingQueue, setPendingQueue] = useState<PendingMessage[]>([])
  const [quickEditTarget, setQuickEditTarget] =
    useState<SelectedContent | null>(null)
  // 弹层 popover 锚点 rect(双击 cell 时传过来,popover 跟随 cell 显示)
  const [quickEditAnchor, setQuickEditAnchor] = useState<{
    left: number
    top: number
    right: number
    bottom: number
  } | null>(null)
  const [quickEditValue, setQuickEditValue] = useState('')
  // 已选图片 — 单格 image 模式和批量 image-batch 模式统一用数组:
  //   - 单格 image:用户可以加多张图给 AI 做参考(prompt 里"换成/添加这些图")
  //   - 批量 image-batch:按顺序映射到 N 个 cell,不够循环
  // 长度上限由 input 端拦截(批量 = cell 数;单格 = 配合"建议 ≤ 30 张")
  const [quickEditImages, setQuickEditImages] = useState<
    QuickEditImageAttachment[]
  >([])
  const quickEditImagesInputRef = useRef<HTMLInputElement>(null)
  // 文字模式 textarea ref:Modal 打开后用 useEffect 强制聚焦,
  // 避免 autoFocus 被 Modal mount 动画抢走焦点的边缘场景
  const quickEditTextAreaRef = useRef<any>(null)
  // 团队相册 picker 弹窗:popover 内点"团队相册选图"卡片时弹出
  const [teamPickerTarget, setTeamPickerTarget] = useState<{
    content: SelectedContent
    mode: 'image' | 'image-batch'
    anchor?: { left: number; top: number; right: number; bottom: number }
  } | null>(null)

  // ─── SSE result 埋点辅助 ref ───────────────
  // lastSendAtRef:用户每次 send 时记 timestamp,onDone 时算 costTime
  // firstAssistantDoneRef:第一次 onDone 视为 recognize_doc(初次 AI 识别),后续视为
  //   ai_fill(toolNames 含 excel_edit/word_edit) 或 ai_chat_response(普通对话)
  // currentSendAiFillKindRef:本轮 SSE 是 quick action 触发时,带上选中类型 kind
  //   (column/row/text_cell/image_cell_empty/image_cell_filled),用于 ai_fill
  //   result.string1.dequeue 时从 PendingMessage.aiFillSelectionKind 写入,
  //   onDone 读取后清空.普通用户 chat 不带,留空字符串.
  const lastSendAtRef = useRef<number>(0)
  const firstAssistantDoneRef = useRef<boolean>(true)
  const currentSendAiFillKindRef = useRef<string>('')

  /** 从 messages 末尾抽最后一条 assistant 消息的纯文本(串接所有 text part). */
  const extractLastAssistantText = (msgs: ChatMessage[]): string => {
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role !== 'assistant') continue
      const text = (msgs[i].parts || [])
        .filter((p: any) => p && p.type === 'text')
        .map((p: any) => p.text || '')
        .join('')
      if (text) return text
    }
    return ''
  }

  const { messages, setMessages, sending, send, abort } = useSSEChat({
    onDone: ({ toolNames }) => {
      // 每轮会话完成后刷新积分余额；余额耗尽（<= 0）时弹「积分不足」
      refreshCredits().then((c) => {
        if (c && c.available != null && c.available <= 0) {
          setCreditsExhaustedVisible(true)
        }
      })
      // 预览刷新已上移到"per edit tool output-available"的 useEffect,
      // 这里不再重复 triggerPreviewRefresh(避免一轮 SSE 结束时再刷一次冗余).
      // result 埋点 — 三选一(recognize_doc / ai_fill / ai_chat_response)
      const costTime = lastSendAtRef.current
        ? Date.now() - lastSendAtRef.current
        : 0
      const sid = readSessionId()
      // 用 setState 函数式 callback 读 fresh messages — onDone 触发时常规
      // messages 引用是 stale(SSE chunk 是 async,setState 还在 batch),
      // 走 setter 当 getter 模式拿最新.callback 返回 curr 不修改.
      let aiReplyText = ''
      setMessages((curr) => {
        aiReplyText = extractLastAssistantText(curr)
        return curr
      })
      // 取本轮 SSE 关联的 selection kind(只在 ai_fill 用),读完清掉
      const aiFillKind = currentSendAiFillKindRef.current
      currentSendAiFillKindRef.current = ''

      if (firstAssistantDoneRef.current) {
        // 第一次 AI 响应 = 初次识别文档
        firstAssistantDoneRef.current = false
        trackResult('formFilling', 'recognize_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
          sessionID: sid,
          actionResult: 'success',
          costTime
        })
      } else if (toolNames.some((n: string) => editToolSetRef.current.has(n))) {
        // AI 跑了编辑工具 = ai_fill
        // string1 = 选中类型(quick action 触发时带, chat 触发时空)
        // string2 = AI 回复内容描述(取末条 assistant text)
        trackResult('formFilling', 'ai_fill', {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
          sessionID: sid,
          actionResult: 'success',
          costTime,
          string1: aiFillKind,
          string2: aiReplyText
        })
      } else {
        // 普通 AI 回复 = ai_chat_response
        // ⚠ string1 神策侧锁枚举(同 ai_fill 选中类型),不能塞 AI 回复内容文本.
        // 内容靠后端 sessionID 关联对话历史查询,不走埋点
        trackResult('formFilling', 'ai_chat_response', {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
          sessionID: sid,
          actionResult: 'success',
          costTime
        })
      }
    },
    onError: (e) => {
      // eslint-disable-next-line no-console
      console.error('[useSSEChat]', e)
      const costTime = lastSendAtRef.current
        ? Date.now() - lastSendAtRef.current
        : 0
      const errMsg = e instanceof Error ? e.message : String(e)
      const sid = readSessionId()
      // 失败也读一下 fresh messages(虽然多半为空,但 SSE 中途断时可能已有部分文本)
      let aiReplyText = ''
      setMessages((curr) => {
        aiReplyText = extractLastAssistantText(curr)
        return curr
      })
      const aiFillKind = currentSendAiFillKindRef.current
      currentSendAiFillKindRef.current = ''
      // 失败 result:沿用同一 routing(第一次失败归 recognize_doc,后续归 ai_chat_response)
      if (firstAssistantDoneRef.current) {
        firstAssistantDoneRef.current = false
        trackResult('formFilling', 'recognize_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
          sessionID: sid,
          actionResult: 'fail',
          costTime,
          errorMsg: errMsg
        })
      } else if (aiFillKind) {
        // 失败前有 quick action 上下文 → 归 ai_fill 失败
        trackResult('formFilling', 'ai_fill', {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
          sessionID: sid,
          actionResult: 'fail',
          costTime,
          string1: aiFillKind,
          string2: aiReplyText,
          errorMsg: errMsg
        })
      } else {
        // ⚠ ai_chat_response 同上,string1 不塞 AI 回复内容(神策枚举校验)
        trackResult('formFilling', 'ai_chat_response', {
          requirement_id: 1103,
          track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
          sessionID: sid,
          actionResult: 'fail',
          costTime,
          errorMsg: errMsg
        })
      }
    }
  })

  // ─── 切团队 / 切会话 → abort 旧 run (避免 zombie 在后端继续烧 token) ───
  //
  // 背景: useSSEChat 在 SSE 流首事件抽 runId 存 ref, **依赖父组件在切换时调 abort**
  // 把旧 runId 传给 /api/chat/abort. 不主动 abort 的话, 切团队/会话后旧 LLM
  // 在 Server / openclaw 端继续跑到自然完成, 烧 token + sessions 列表"思考中" hang.
  //
  // 两个 effect 拆开是为了**精确归因** — 团队和会话切换各自触发一次, 不耦合.
  // 首次 mount 用 ref baseline === null 跳过 (没旧 run 可 abort).
  //
  // Server 端 abort_run (apps/chat/service.py:461) 不校验 group_id, 只看 runId 全局
  // 唯一; 跨 team / 跨 conversation 都能命中 _cancel_events 字典. 所以即使切团队后
  // x-team-id header 变了, 旧 runId 也能被 Server 找到并 set().
  const prevTeamRef = useRef<string | null>(null)
  const prevSidRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevTeamRef.current === null) {
      prevTeamRef.current = teamNo
      return
    }
    if (prevTeamRef.current !== teamNo) {
      abort()
      prevTeamRef.current = teamNo
    }
  }, [teamNo, abort])
  useEffect(() => {
    if (prevSidRef.current === null) {
      prevSidRef.current = resumeSessionId
      return
    }
    if (prevSidRef.current !== resumeSessionId) {
      abort()
      prevSidRef.current = resumeSessionId
    }
  }, [resumeSessionId, abort])

  // ───────── 按"单个 edit tool 完成"刷新预览 ─────────
  // 改造前:onDone(SSE 整轮结束) 才 triggerPreviewRefresh,一轮里 AI 可能跑
  //   N 个 edit tool,用户要等整轮跑完才看到任何中间结果.
  // 改造后:每个 excel_edit / word_edit 进入 output-available 状态时立即刷一次,
  //   用 refreshedToolCallsRef Set 记录已处理 toolCallId 避免重复刷.
  // 注:onDone 不再触发 refresh(per-tool 已覆盖). 用户手动点"刷新预览"按钮
  //   仍走 triggerPreviewRefresh 同一通道.
  // 首次加载历史(resume session)时:历史 messages 里已有完成的 edit tool,
  // 此时只把它们填进 Set 标记为"已处理",不触发刷新(否则会无意义重拉预览文件).
  const firstMessagesScanRef = useRef(true)
  // ── 编辑蒙版的"延伸 hold" ─────────────────────
  // 蒙版生命周期 = AI 编辑中 + 触发 refresh 后等到新文档加载完成 / 失败.
  // docActionState.phase === 'editing' 只覆盖前半段(tool 还在跑),
  // tool output-available 后 phase 立即进入 ready/staleReady,但此时预览
  // 还在 buildPreviewUrl(refreshNonce) → useFileLoader 重新 fetch,用户看
  // 到的还是旧文档,不能让蒙版立刻消失.
  //
  // 蒙版两态:
  //   - phase === 'editing'  → "AI 正在编辑文档…"
  //   - maskHold && !editing → "正在同步最新文档…"(带取消按钮)
  //
  // 触发条件:
  //   - per-tool useEffect 触发 refresh 时 setMaskHold(true)
  //   - previewLoadingState.busy 由 true 转 false 时 setMaskHold(false)
  //     (loading 在成功 / 失败两种情况都会落回 false,所以两种结果都能正常清)
  //   - 用户点蒙版上的"取消"按钮 → 直接 setMaskHold(false),后台 fetch 继续
  //
  // 不再用时间兜底:大文档下载可能很慢(实测 30s 都未必够),给用户取消按钮
  // 比按时强清更可控.
  const [maskHold, setMaskHold] = useState(false)
  useEffect(() => {
    let touched = false
    for (const msg of messages) {
      if (msg.role !== 'assistant') continue
      for (const part of msg.parts) {
        if (part.type !== 'tool-call') continue
        if (!editToolSet.has(part.toolName)) continue
        if (part.state !== 'output-available') continue
        const id = part.toolCallId
        if (!id) continue
        if (refreshedToolCallsRef.current.has(id)) continue
        refreshedToolCallsRef.current.add(id)
        touched = true
      }
    }
    if (firstMessagesScanRef.current) {
      firstMessagesScanRef.current = false
      return
    }
    if (touched) {
      triggerPreviewRefresh()
      setMaskHold(true)
    }
  }, [messages, triggerPreviewRefresh, editToolSet])

  // 预览 loading 由 true → false (新文档下载 + 渲染都完成 / 失败) 时清 maskHold
  const prevPreviewBusyRef = useRef(false)
  useEffect(() => {
    const prev = prevPreviewBusyRef.current
    const curr = previewLoadingState.busy
    prevPreviewBusyRef.current = curr
    if (prev && !curr) {
      setMaskHold(false)
    }
  }, [previewLoadingState.busy])

  // 用户点蒙版"知道了"按钮:dismiss 蒙版,后台 fetch 不打断.
  //   - 浏览器 fetch 已经触发,abort 也救不回带宽,所以只是 UX 出口
  //   - 关掉蒙版后给 toast 解释:下载完成会自动刷新,建议等新版再发起下一轮编辑
  //   - 完成时 prevPreviewBusyRef 仍会 true→false 一次,但 maskHold 早是 false
  //     →setMaskHold(false) 是 no-op,不会再弹回来
  const handleDismissSync = useCallback(() => {
    setMaskHold(false)
    message.info(
      '文档仍在后台同步,完成后会自动刷新。建议等新版就绪再发起下一轮修改,避免基于旧版操作。',
      4
    )
  }, [])

  // 给 handleSend 用:在 SSE 进行中入队时弹 toast 让用户感知"已排队"
  // (handleSend deps 为空避免重建,所以走 ref 读最新值)
  const sendingRef = useRef(sending)
  // 出队调度的同步再入锁:出队 effect 的依赖里含 send,而 useSSEChat 每次 render
  // 都返回新的 send 引用(其 deps 有内联 options 对象),effect 会随 send 身份变化
  // 重跑;send 的 `if (sending) return` 是 state 守卫(异步,setSending 未提交前
  // 恒读到 false),挡不住同一 tick 内的第二次触发 → 一次点击发两条(RFCT-56)。
  // 这里用 ref 做同步锁:发出即置 true,SSE 回到空闲(sending=false)再解锁。
  const isDispatchingRef = useRef(false)
  useEffect(() => {
    sendingRef.current = sending
    // SSE 结束回到空闲态:释放出队锁,允许下一条排队消息发送
    if (!sending) isDispatchingRef.current = false
  }, [sending])
  const pendingQueueLenRef = useRef(0)
  // 三件套（导出/保存模板/分享）已经从消息流挪到左侧文档面板的 footer，
  // 此处只负责"清理已写到 message 上的旧 file attachment"，不再注入新的。
  // 旧逻辑（latestEligibleIndex + 注入 file attachment）见 git 历史 b39894a2 之前。
  const displayMessages = useMemo(() => {
    const attachmentUrl = toAbsoluteUrl(previewBaseUrl || fileUrl)
    if (!attachmentUrl) return messages
    const normalizedAttachmentUrl = stripPreviewNonce(attachmentUrl)
    return messages.map((message) => {
      const existing = message.attachments || []
      const cleaned = existing.filter((att) => {
        if (att.type !== 'file') return true
        if (att.data?.name !== fileName) return true
        return (
          stripPreviewNonce(att.data?.url || '') !== normalizedAttachmentUrl
        )
      })
      return cleaned.length === existing.length
        ? message
        : { ...message, attachments: cleaned }
    })
  }, [messages, previewBaseUrl, fileUrl, fileName])

  // ────────── 文档操作状态派生 ──────────
  // (docTick / lastPreviewSyncAtRef 已上移到 refreshNonce 旁,跟刷新预览挂钩)

  // 三件套（导出 / 保存模板 / 分享）+ 状态徽标的出现时机：
  // 等到 messages 中至少存在一条「非 streaming 且含 excel_edit / word_edit 工具调用」的 assistant 消息后才挂出，
  // 一旦挂出后就一直保留（出现后即驻留）。
  const hasEditableDocTool = useMemo(() => {
    return messages.some(
      (m) =>
        m.role === 'assistant' &&
        !m.streaming &&
        m.parts.some(
          (p) => p.type === 'tool-call' && editToolSet.has(p.toolName)
        )
    )
  }, [messages, editToolSet])

  const docActionState = useMemo<DocActionState>(() => {
    if (!fileUrl) return { phase: 'idle' }

    let activeEditing = false
    let activeHint: string | undefined
    let latestPart: MessagePart | null = null

    for (const message of messages) {
      if (message.role !== 'assistant') continue
      const isStreaming = !!message.streaming
      for (const part of message.parts) {
        if (part.type !== 'tool-call' || !editToolSet.has(part.toolName))
          continue
        latestPart = part
        if (
          part.state === 'input-streaming' ||
          part.state === 'input-available' ||
          (isStreaming && part.state !== 'output-error')
        ) {
          activeEditing = true
          // 尝试从 args 抠出"r5-r7"之类的范围当 hint
          const args = part.args as Record<string, unknown> | undefined
          const range =
            args &&
            (typeof args.range === 'string'
              ? args.range
              : typeof args.cell_range === 'string'
                ? args.cell_range
                : typeof args.row === 'string'
                  ? args.row
                  : undefined)
          if (range && !activeHint) activeHint = range
        }
      }
    }

    if (activeEditing) {
      return { phase: 'editing', editingHint: activeHint }
    }

    if (!latestPart || latestPart.type !== 'tool-call') {
      return { phase: 'idle' }
    }

    if (latestPart.state === 'output-error') {
      return { phase: 'error', errorMessage: latestPart.errorText }
    }

    if (latestPart.state !== 'output-available' || !latestPart.endedAt) {
      // 边界保护：状态既不算 active 也没有 endedAt，回退 idle
      return { phase: 'idle' }
    }

    const endedAt = latestPart.endedAt
    const elapsed = Date.now() - endedAt
    if (elapsed < READY_DEBOUNCE_MS) {
      return { phase: 'editing', latestEditEndedAt: endedAt }
    }

    // AI 改完(有 endedAt)且预览还没同步过(ref=0)或预览同步时刻早于本次 edit
    // → staleReady("AI 改了但预览还没刷新过"). 用户点刷新预览就 refresh ref.
    if (endedAt > lastPreviewSyncAtRef.current) {
      return { phase: 'staleReady', latestEditEndedAt: endedAt }
    }

    return { phase: 'ready', latestEditEndedAt: endedAt }
    // docTick 故意作为 dep：防抖结束时 setDocTick 触发重算
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, fileUrl, docTick, editToolSet])

  // 防抖窗口内调度一次 tick，让上面的 useMemo 在 1.5s 后从 editing → ready
  useEffect(() => {
    if (
      docActionState.phase !== 'editing' ||
      !docActionState.latestEditEndedAt
    ) {
      return
    }
    const elapsed = Date.now() - docActionState.latestEditEndedAt
    if (elapsed < READY_DEBOUNCE_MS) {
      const t = setTimeout(
        () => setDocTick((x) => x + 1),
        READY_DEBOUNCE_MS - elapsed + 50
      )
      return () => clearTimeout(t)
    }
  }, [docActionState.phase, docActionState.latestEditEndedAt])

  // 会话初始化（只缓存 sessionId，不缓存消息内容）：
  //   1) URL 带 ?sessionId=xxx → 优先用它（"最近会话"列表点进来），写到 localStorage，拉后端历史
  //   2) URL 干净但 localStorage 有 sessionId → 刷新场景，用它拉后端历史
  //   3) 都没有 → 真新会话，留空白，useSSEChat 首次发消息时生成新 UUID 写入 localStorage
  //
  // 进入新会话由两条路径主动清 localStorage 触发：
  //   - 首页"上传文件"成功后 → 跳转前清
  //   - work 页"再填一份新的"按钮 → 跳转前清
  const initDoneRef = useRef(false)
  useEffect(() => {
    if (initDoneRef.current) return
    initDoneRef.current = true

    let sid = resumeSessionId
    if (!sid) {
      try {
        sid = window.localStorage.getItem(CONV_KEY) || ''
      } catch {
        /* 忽略 */
      }
    } else {
      try {
        window.localStorage.setItem(CONV_KEY, sid)
      } catch {
        /* 忽略 */
      }
    }
    if (!sid) return
    xheyAi
      .getSessionHistory(sid)
      .then((raw) => {
        setMessages(hydrateHistory(raw))
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[history] 加载失败', err)
      })
  }, [resumeSessionId, setMessages])

  const autoSentRef = useRef(false)

  // 加入到会话 / quick action 统一：**替换**掉旧的附件
  // 暂只支持单一附件（多附件以后再说）
  const handleContentSelected = useCallback((content: SelectedContent) => {
    setQuotes([
      {
        ...content,
        id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      }
    ])
  }, [])

  // mode 可选:
  //   - 双击 cell 触发时无 mode,modal 按 cell 自身 image/text 状态推断
  //   - 浮窗"添加图片/换图/添加文字/修改"触发时 mode='image'|'text',强制指定形态
  //     (content 上已被 DocumentPreview 注入 quickEditMode 字段,isImageCellContent
  //      会优先看它,这里的 mode 参数仅供 callsite 显式声明意图)
  const handleCellQuickEdit = useCallback(
    (
      content: SelectedContent,
      mode?: 'image' | 'image-batch' | 'text',
      anchor?: { left: number; top: number; right: number; bottom: number }
    ) => {
      const firstCell = content.rangePayload?.cells[0]?.[0]
      // 文字 cell 双击 / "修改":预填当前值;其他场景起始为空
      const effectiveMode =
        mode ??
        content.quickEditMode ??
        (isImageCellContent(content) ? 'image' : 'text')
      // displayText 是裸文本,docx-preview 用 CSS counter ::before 渲染的
      // 1./2./3. 编号(numPr 列表)没在里头。优先用 getRenderedCellTextForEditModal
      // 反查 DOM 拿带编号的渲染态文本,跟用户在预览里看到的一致;DOM 查不到
      // 再回退 displayText
      const isTextPrefill =
        effectiveMode === 'text' &&
        firstCell &&
        !firstCell.isEmpty &&
        !firstCell.imageSrc &&
        firstCell.displayText !== '[图片]'
      // docx 非表格"文字选区"(`source === 'text-selection'`,无 rangePayload):
      // 走"修改" action 时,把用户选中的原文 content.text 预填进 textarea,
      // 让用户在原文基础上改而不是从空白起.
      const isPlainTextSelection =
        effectiveMode === 'text' &&
        content.source === 'text-selection' &&
        typeof content.text === 'string' &&
        content.text.length > 0
      const initialValue = isPlainTextSelection
        ? content.text || ''
        : isTextPrefill
          ? (getRenderedCellTextForEditModal(content) ??
            firstCell.displayText ??
            '')
          : ''
      setQuickEditTarget(content)
      setQuickEditAnchor(anchor || null)
      setQuickEditValue(initialValue)
      setQuickEditImages((prev) => {
        prev.forEach((it) => {
          if (it.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(it.previewUrl)
          }
        })
        return []
      })
    },
    []
  )

  /** popover 右栏"团队相册选图"卡片 → 弹 team picker(继承 popover 当前的 target) */
  const handleOpenTeamPicker = useCallback(() => {
    if (!quickEditTarget) return
    // 换图选图半屏:点击"团队相册选图" click 埋点
    trackClick('imagePicker', 'team_album', {
      requirement_id: 1103,
      track_sign: 'aai.b8138.click.ai_click_web_ai_image_picker',
      sessionID: readSessionId()
    })
    const mode: 'image' | 'image-batch' =
      quickEditTarget.quickEditMode === 'image-batch' ? 'image-batch' : 'image'
    setTeamPickerTarget({
      content: quickEditTarget,
      mode,
      anchor: quickEditAnchor || undefined
    })
  }, [quickEditTarget, quickEditAnchor])

  const handleTeamPickerCancel = useCallback(() => {
    setTeamPickerTarget(null)
  }, [])

  /**
   * 团队 picker 完成:
   *   - 添加图片 / 换图(单格或批量)场景:**选完即发**,绕过 popover 回显
   *   - 其他场景(文字 cell):append 到 quickEditImages,popover 内 grid 回显让用户加描述
   */
  const handleTeamPickerConfirm = useCallback(
    (photos: TeamPhotoPickResult[]) => {
      const target = teamPickerTarget
      if (!target) return
      setTeamPickerTarget(null)

      // 非表格图片选区"换图":没有 cell 概念,选完即发(语义和 cell 换图一致)。
      // 支持单张和拖选多张:imageSrcs.length 决定旧图描述。
      // src 列表走 quote 块,不写进 message text;ViewPath 在 text 里写
      // body/p[N]/img[M] 位置,让 agent 按路径走 docx tree 找元素。
      if (target.content.source === 'image-selection') {
        const oldN = target.content.imageSrcs?.length || 0
        const oldPaths = (target.content.imageViewPaths || []).filter(Boolean)
        const oldDesc =
          oldN > 1
            ? oldPaths.length === oldN
              ? `以下 ${oldN} 张图片(${oldPaths.join(', ')})`
              : `这 ${oldN} 张图片`
            : oldPaths.length === 1
              ? `图片 ${oldPaths[0]}`
              : '这张图片'
        const attachments = photos.map((p) => ({
          url: p.url,
          name: p.name,
          mediaType: p.mediaType
        }))
        let action = ''
        if (photos.length === 1 && oldN <= 1) action = '把图片换成这张'
        else if (photos.length === 1 && oldN > 1)
          action = `用这张图把这 ${oldN} 张全替换`
        else if (photos.length > 1 && oldN <= 1)
          action = `参考这些图片(共 ${photos.length} 张)选一张最合适的替换`
        else
          action = `按顺序对应替换(共 ${photos.length} 张新图对应 ${oldN} 张旧图,不足循环)`
        const messageText = `把文档里${oldDesc}。要求:${action}`
        handleSend(
          messageText,
          [
            {
              ...target.content,
              id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
            }
          ],
          attachments,
          pickAiFillSelectionKind(target.content)
        )
        closeQuickEditModal()
        return
      }

      // 选完即发场景:空 cell 添加 / 图片 cell 换图,单格 + 批量都直接发,不进 popover 回显
      const cs = target.content.cellState
      const isAddingFlow = cs === 'empty' || cs === 'all-empty'
      const isReplaceFlow = cs === 'image' || cs === 'all-image'
      if (isAddingFlow || isReplaceFlow) {
        const attachments = photos.map((p) => ({
          url: p.url,
          name: p.name,
          mediaType: p.mediaType
        }))
        const rangeRef =
          target.content.rangePayload?.range ||
          target.content.cellRef ||
          '选中区域'
        const isBatch = target.mode === 'image-batch'
        const verb = isAddingFlow ? '添加' : '替换为'
        // 单格 + 批量 / 添加 + 换图 四种组合,组装一条清晰的指令给 AI
        const action = isBatch
          ? `${verb}这些图片(共 ${photos.length} 张,按 左→右、上→下 顺序映射;图片不足时循环重复)`
          : photos.length === 1
            ? `${verb}这张图片`
            : `${verb}这些图片(共 ${photos.length} 张参考图)`
        const messageText = isBatch
          ? `填 ${rangeRef} 区域的图片。要求：${action}`
          : `填 ${rangeRef} 这一格。要求：${action}`
        handleSend(
          messageText,
          [
            {
              ...target.content,
              id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
            }
          ],
          attachments,
          pickAiFillSelectionKind(target.content)
        )
        closeQuickEditModal()
        return
      }

      // 其他场景(换图 / 批量):仍走 quickEditImages 回显,让用户加描述后手动发
      const next: QuickEditImageAttachment[] = photos.map((p) => ({
        fileName: p.name,
        fileUrl: p.url,
        mediaType: p.mediaType,
        previewUrl: p.url,
        status: 'done'
      }))
      setQuickEditImages((prev) => {
        if (target.mode === 'image-batch') {
          // 批量:整片替换(N 张图按顺序映射 N 个 cell 是核心语义,不该混叠)
          prev.forEach((it) => {
            if (it.previewUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(it.previewUrl)
            }
          })
          return next
        }
        // 单格换图:追加到现有图集后(用户可能先本地传几张再团队选几张)
        const seenUrls = new Set(prev.map((it) => it.fileUrl))
        const toAppend = next.filter(
          (it) => !it.fileUrl || !seenUrls.has(it.fileUrl)
        )
        return [...prev, ...toAppend]
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamPickerTarget]
  )

  const handleRemoveQuote = useCallback((id: string) => {
    setQuotes((prev) => prev.filter((q) => q.id !== id))
  }, [])

  const handleSend = useCallback(
    (
      text: string,
      attachedQuotes: QuoteItem[],
      composerAttachments: Array<{
        url: string
        name: string
        mediaType: string
      }> = [],
      /** 可选:quick action(send-now)触发时传选中类型,带到 PendingMessage 上,
       *  出队 send 时写入 currentSendAiFillKindRef → onDone 时拿来上报
       *  ai_fill result 的 string1.普通用户 chat 不传. */
      aiFillSelectionKind?: string
    ) => {
      // send_message 上报:无论用户主动 chat 还是 quick action 程序自动发,
      // 都归到本事件(共用 send 通道,行为语义统一为"提交一条 prompt 给 AI")
      // ⚠ string1 神策枚举严校(column/row/text_cell/...),消息内容塞不进去
      // → 不传 string1,内容靠后端 sessionID 关联对话历史查
      trackClick('formFilling', 'send_message', {
        requirement_id: 1103,
        track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
        sessionID: readSessionId()
      })
      // 入队 — 不直接调 send。出队的时机由下面 useEffect 监听 sending=false 来决定:
      //   - 当前空闲 + 队列非空:取队首发送
      //   - SSE 进行中:消息留队列等候,用户可单条取消
      // currentDocPath / isFirstMessage 在出队时才算(基于实时 messages.length / file)
      const finalMessage = buildMessageWithQuotes(text, attachedQuotes)
      const pending: PendingMessage = {
        id: `pq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        text,
        message: finalMessage,
        attachments:
          composerAttachments.length > 0 ? composerAttachments : undefined,
        aiFillSelectionKind
      }
      // SSE 进行中 / 前面还有排队消息 → 不会立即发送,弹 toast 让用户感知"已排队"
      // (典型场景:右侧 AI 思考中,用户在左侧文档点"换图/删除/AI 帮填"看不到反馈)
      if (sendingRef.current || pendingQueueLenRef.current > 0) {
        message.info('已提交改动,在计划中排队修改...')
      }
      setPendingQueue((prev) => [...prev, pending])
      setQuotes([])
    },
    []
  )

  // 单条取消:从等候队列中删除指定 id
  const handleCancelPending = useCallback((id: string) => {
    setPendingQueue((prev) => prev.filter((p) => p.id !== id))
  }, [])

  // 出队调度:SSE 空闲(sending=false) + 队列非空时,取队首发送
  // 注意:出队时计算 currentDocPath 用最新 messages.length(可能因前一轮 SSE
  // 完成后已经累积了消息);避免用入队时刻的 stale 值
  useEffect(() => {
    pendingQueueLenRef.current = pendingQueue.length
    if (sending) return
    // 同步再入锁:上一次已发出但 sending state 还没提交时,effect 因 send 身份
    // 变化重跑会读到 sending=false,此处用 ref 挡住,避免一次点击发两条(RFCT-56)
    if (isDispatchingRef.current) return
    if (pendingQueue.length === 0) return
    const next = pendingQueue[0]
    isDispatchingRef.current = true
    setPendingQueue((prev) => prev.slice(1))
    const isFirstMessage = messages.length === 0
    const currentDocPath =
      isFirstMessage && file && file.filePath ? file.filePath : undefined
    // 记录本轮 send 起始时间,onDone/onError 用它算 costTime
    lastSendAtRef.current = Date.now()
    // 把本条 pending 关联的 ai_fill 选中类型放进 ref,onDone 时上报
    // (普通用户 chat 无 aiFillSelectionKind,留空 → result 走 ai_chat_response)
    currentSendAiFillKindRef.current = next.aiFillSelectionKind || ''
    send({
      message: next.message,
      attachments: next.attachments,
      currentDocPath
    })
  }, [sending, pendingQueue, messages.length, file, send])

  // quick action:
  //   mode='prefill' (默认) — 替换附件 + 预填到输入框,让用户编辑后手动发
  //   mode='send-now'      — 替换附件 + 直接发送(插入列/二次确认通过的删除等)
  // 定义放在 handleSend 之后,避免 TDZ('used before declaration')
  const handleQuickAction = useCallback(
    (
      content: SelectedContent,
      prompt: string,
      mode: 'prefill' | 'send-now' = 'prefill'
    ) => {
      const quote: QuoteItem = {
        ...content,
        id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      }
      if (mode === 'send-now') {
        // 直接发送:不预填到输入框,quote 通过 handleSend 携带.
        // 同时把选中类型(column/row/text_cell/image_cell_empty/image_cell_filled)
        // 透传给 handleSend → PendingMessage → dequeue 时写 currentSendAiFillKindRef
        // → onDone 时上报 ai_fill result 的 string1 字段(对齐 spec).
        const selectionKind = pickAiFillSelectionKind(content)
        handleSend(prompt, [quote], [], selectionKind)
        return
      }
      setQuotes([quote])
      setPrefillInput({ text: prompt, nonce: Date.now() })
    },
    [handleSend]
  )

  // 上传完成后首次进入 work 页：自动发一条引导消息，让 AI 先识别文档结构。
  // 只在"新会话 + 已加载文件 + 当前消息为空"时触发，续会话 / 已发消息都跳过。
  // 如果用户在 home 选了"参考样例"，会话存储里会有 diff 文本——把它拼进首条消息，
  // 消费完即清掉（避免下一会话误带）。
  useEffect(() => {
    if (autoSentRef.current) return
    // 有 fileUrl (常规上传入口) 或有 skillTitle (「继续使用」入口) 任一即可触发首条消息
    if (!fileUrl && !skillTitle) return
    if (resumeSessionId) return
    let storedSid = ''
    try {
      storedSid = window.localStorage.getItem(CONV_KEY) || ''
    } catch {
      /* 忽略 */
    }
    if (storedSid) return
    if (messages.length > 0) return
    if (sending) return
    autoSentRef.current = true

    // 消费 home 页写入的"参考样例"载荷：diff 文本 + 附件元信息（URL/name/mediaType）
    let sampleDiffText = ''
    let sampleAttachment: {
      url: string
      name: string
      mediaType: string
    } | null = null
    try {
      const raw = window.sessionStorage.getItem('aiClerk:samplePayload')
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed.diffText === 'string') {
          sampleDiffText = parsed.diffText
        }
        if (
          parsed &&
          parsed.attachment &&
          typeof parsed.attachment.url === 'string'
        ) {
          sampleAttachment = parsed.attachment
        }
        window.sessionStorage.removeItem('aiClerk:samplePayload')
      }
    } catch {
      window.sessionStorage.removeItem('aiClerk:samplePayload')
    }

    // 「没有现成文档→描述生成」入口:消费 home 写入的用户描述,作为首条消息直接发给 AI
    let describePrompt = ''
    try {
      describePrompt =
        window.sessionStorage.getItem('aiClerk:describePrompt') || ''
      if (describePrompt) {
        window.sessionStorage.removeItem('aiClerk:describePrompt')
      }
    } catch {
      /* 忽略 */
    }

    // 消费 home 页用户填的"业务背景" — 一句话告诉 agent 这是什么行业/什么文档.
    // agent 在 Step 1 看大纲前先读这段当业务锚, 避免对"清淤前""整改后效果"这类
    // 垂直行业术语字面理解 (LLM 没见过水务/施工等领域).
    // 空 → 不注入, agent 走原 fallback 自己看大纲猜.
    let businessContext = ''
    try {
      const raw = window.sessionStorage.getItem('aiClerk:businessContext')
      if (raw && typeof raw === 'string') {
        businessContext = raw.trim()
      }
      window.sessionStorage.removeItem('aiClerk:businessContext')
    } catch {
      window.sessionStorage.removeItem('aiClerk:businessContext')
    }
    // 业务背景片段: 跟 <aiclerk-sample-context> 同款 hidden marker pattern,
    // ChatPanel 渲染气泡时会剥掉, 用户视角看不到, 但 LLM 在 history 里看得见.
    const businessContextSegment = businessContext
      ? `\n\n<business-context>${businessContext}</business-context>`
      : ''

    // 包在 <aiclerk-sample-context> 里——气泡渲染会剥掉这一段，用户气泡只显示
    // "帮我填写..." 和附件 chip；但 history replay 时这段还在 parts 里，agent 后续
    // 轮次仍能看到上下文。同 home/index.scss 已存在的 <message-attachments>
    // / <current-doc> 处理方式一致。
    //
    // 三种情形：
    //   1. Excel + diffText：贴结构化字段映射，agent 优先按映射填
    //   2. Word + 仅附件：让 agent 用 read 工具自己读样例分析（因为 Word 流式段落
    //      没法做出 Excel 那样紧凑的字段映射）
    //   3. 无样例：常规 prompt
    // 整条用 <aiclerk-task-init> 包裹: 这是 work 页打开自动构造的 task trigger,
    // 不是用户主动输入. ChatPanel 渲染时识别 marker → 整条 user 气泡不显示
    // (用户视角: 进 work 页 = AI 自动开始, 不看到 trigger prompt).
    // LLM 仍正常收到 (history 持久化 + 后续轮上下文完整).
    // 跟仓库现有 hidden marker pattern 一致 (<aiclerk-sample-context> / <message-attachments> / <current-doc>).
    let firstMessage: string
    if (describePrompt) {
      // 描述生成入口:用户没有现成文档,上传的是空白文档。把用户描述作为首条消息直接
      // 展示(不藏 task-init marker),AI 据此在空白文档上建表并填好。
      //
      // 空白表引导已下沉到后端: Claw 按 <aiclerk-scene>=blank_describe 自行组装注入
      // (见 XHeyClaw entry-scene.ts), 前端不再随消息塞 <aiclerk-blank-guide>, 避免前后端
      // 两套引导重复/劈叉。前端只发用户描述 + 末尾 <aiclerk-scene> 场景标记(下方追加)。
      firstMessage = describePrompt
    } else if (!fileUrl && skillTitle) {
      // 「继续使用」入口: 没文件, 复用技能开新会话. agent 加载技能 SKILL.md +
      // references (template + filled_sample) 后按技能流程主动问用户需要什么数据.
      // skill 工具的 name 必须用 ASCII slug (skillName), 不能用中文 (Server NAMING 校验).
      const nameHint = skillName
        ? `\n\n技能 name 标识 (调 skill 工具时用这个, 不是中文名): \`${skillName}\``
        : ''
      firstMessage = `<aiclerk-task-init>请使用「${skillTitle}」技能完成本次工作。${nameHint}\n\n按技能 SKILL.md 流程问我需要什么数据 (e.g. 时间范围 / 关键词 / 上传新模板等), 我提供后你直接做完.${businessContextSegment}</aiclerk-task-init>`
    } else if (sampleDiffText) {
      firstMessage = `<aiclerk-task-init>帮我填写这份${fileName}。${businessContextSegment}\n\n<aiclerk-sample-context>\n我把"参考样例"作为附件传给你了（见消息附件）。下面这段是前端用 ExcelJS 解析样例后提炼出的结构化字段映射——优先看这段、按它填模板；如果某些信息映射不到再去 read 附件原文件。\n\n${sampleDiffText}\n</aiclerk-sample-context></aiclerk-task-init>`
    } else if (sampleAttachment) {
      firstMessage = `<aiclerk-task-init>帮我填写这份${fileName}。${businessContextSegment}\n\n<aiclerk-sample-context>\n我把一份"参考样例"作为附件发给你了（见消息附件，文件名：${sampleAttachment.name}）。请你先用 read 工具读取这份样例，理解它的填写风格、字段含义、段落结构和表格内容，再按相同风格填到模板对应位置。如果样例里有图片或特殊嵌入对象，先告诉我图片来源后再填；不要直接跳过样例自己猜着填。\n</aiclerk-sample-context></aiclerk-task-init>`
    } else {
      firstMessage = `<aiclerk-task-init>帮我填写这份${fileName}${businessContextSegment}</aiclerk-task-init>`
    }
    // 「继续使用」入口走上面 skillTitle 分支 (单独 message). 老入口 (有文件) 如果也带
    // skillTitle, 在 task-init 内 prepend 技能 hint.
    if (skillTitle && fileUrl) {
      firstMessage = firstMessage.replace(
        /^<aiclerk-task-init>/,
        `<aiclerk-task-init>请使用「${skillTitle}」技能完成本次工作。\n\n`
      )
    }

    // 显式声明入口场景, 供后端精确路由: blank_describe → 走"空白表先设计表结构、
    // 默认带图、不发范围卡"的新引导; upload_doc / template_reuse → 各自既有流程不变.
    // 后端「有则用、无则回退老逻辑」, 可独立灰度, 漏发/晚发都不会坏. 只发首条
    // (会话起始属性, 后续轮不带). 追加在末尾, 由下方剥离逻辑从可见气泡里去掉.
    const aiClerkScene:
      | 'upload_doc'
      | 'blank_describe'
      | 'template_reuse' = describePrompt
      ? 'blank_describe'
      : skillTitle
      ? 'template_reuse'
      : 'upload_doc'
    firstMessage = `${firstMessage}<aiclerk-scene>${aiClerkScene}</aiclerk-scene>`

    handleSend(
      firstMessage,
      [],
      sampleAttachment
        ? [
            {
              url: sampleAttachment.url,
              name: sampleAttachment.name,
              mediaType: sampleAttachment.mediaType
            }
          ]
        : []
    )
  }, [
    fileUrl,
    resumeSessionId,
    messages.length,
    sending,
    handleSend,
    fileName,
    skillTitle,
    skillName
  ])

  useEffect(() => {
    return () => {
      quickEditImages.forEach((it) => {
        if (it.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(it.previewUrl)
        }
      })
    }
  }, [quickEditImages])

  // quick-edit-modal 打开 + 文字模式时强制 focus textarea(autoFocus 兜底):
  // antd Modal 的进入动画会临时托管焦点,某些场景下 textarea 上的 autoFocus
  // 被覆盖。延迟到动画结束后用 ref 显式 focus,并把光标置于文本末尾,
  // 让"双击 cell → 直接键入"链路始终顺畅
  useEffect(() => {
    if (!quickEditTarget) return
    if (isImageCellContent(quickEditTarget)) return
    const timer = setTimeout(() => {
      const el = quickEditTextAreaRef.current
      if (!el) return
      // antd 3 TextArea ref.focus() 内部会聚焦真实 textarea
      if (typeof el.focus === 'function') el.focus()
      // 把光标移到末尾(预填的当前值场景下 autoFocus 默认会全选,
      // 改为光标在末尾更像"继续编辑")
      const native = el.resizableTextArea?.textArea || el.textAreaRef
      if (native && typeof native.setSelectionRange === 'function') {
        const len = (quickEditValue || '').length
        native.setSelectionRange(len, len)
      }
    }, 80)
    return () => clearTimeout(timer)
    // 仅依赖 quickEditTarget 触发(value 变化不重 focus,避免打断输入)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickEditTarget])

  const handleUserAskSubmit = useCallback(
    (response: {
      pending_id: string
      answers: Record<string, any>
      /** 用户点右上角关闭/遮罩 = 取消本轮：尾部指令换成「停下等指示」，避免模型继续猜 */
      cancelled?: boolean
    }) => {
      const json = JSON.stringify(
        { pending_id: response.pending_id, answers: response.answers },
        null,
        2
      )
      const trailer = response.cancelled
        ? '用户取消了选择(或本轮被中止)。停下来等用户进一步指示,不要自行往下猜。'
        : '确认以上填写计划，请继续执行。'
      const text = `[user_ask_response]\n${json}\n\n${trailer}`
      handleSend(text, [])
    },
    [handleSend]
  )

  const closeQuickEditModal = useCallback(() => {
    quickEditImages.forEach((it) => {
      if (it.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(it.previewUrl)
      }
    })
    setQuickEditTarget(null)
    setQuickEditAnchor(null)
    setQuickEditValue('')
    setQuickEditImages([])
    if (quickEditImagesInputRef.current) {
      quickEditImagesInputRef.current.value = ''
    }
  }, [quickEditImages])

  /** 触发本地多图选择 — 单格 / 批量 modes 共用同一个 input(multiple) */
  const handlePickQuickEditImages = useCallback(() => {
    // 换图选图半屏:点击"本地上传" click 埋点
    trackClick('imagePicker', 'local_upload', {
      requirement_id: 1103,
      track_sign: 'aai.b8138.click.ai_click_web_ai_image_picker',
      sessionID: readSessionId()
    })
    if (quickEditImagesInputRef.current) quickEditImagesInputRef.current.click()
  }, [])

  /** 移除指定索引的图片 + 撤销 blob URL */
  const removeQuickEditImageAt = useCallback((index: number) => {
    setQuickEditImages((prev) => {
      const next = [...prev]
      const [removed] = next.splice(index, 1)
      if (removed?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl)
      }
      return next
    })
  }, [])

  // 多选图片 → 按顺序追加到 quickEditImages,input 端拦截超出 limit:
  //   - 批量(image-batch):limit = 选区有效 cell 数(超出截断,跟语义"N 张图填 N 格"对齐)
  //   - 单格(image):limit = 30(避免给 AI 的 context 过大,跟团队 picker 上限一致)
  const handleQuickEditImagesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (files.length === 0) return

      // 选完即发场景(添加 / 换图 ,单格 + 批量都覆盖):
      // popover 立即关闭,上传在 background;handleSend 入队,SSE 空闲时发出
      const cs = quickEditTarget?.cellState
      const isAddingFlow = cs === 'empty' || cs === 'all-empty'
      const isReplaceFlow = cs === 'image' || cs === 'all-image'
      const isDirectSendFlow =
        (isAddingFlow || isReplaceFlow) && quickEditImages.length === 0
      if (isDirectSendFlow && quickEditTarget) {
        const targetSnapshot = quickEditTarget
        const rangeRef =
          targetSnapshot.rangePayload?.range ||
          targetSnapshot.cellRef ||
          '选中区域'
        const isBatch = targetSnapshot.quickEditMode === 'image-batch'
        const limited = files.slice(0, 30)
        if (files.length > 30) {
          message.warning('最多可添加30张图片')
        }
        closeQuickEditModal()
        Promise.all(
          limited.map(async (file) => {
            const mediaType = file.type || 'image/*'
            if (!mediaType.startsWith('image/')) return null
            try {
              const r = await xheyAi.uploadAttachment(file)
              return {
                url: r.fileUrl,
                name: r.fileName || file.name,
                mediaType
              }
            } catch {
              return null
            }
          })
        ).then((results) => {
          const attachments = results.filter(
            (x): x is { url: string; name: string; mediaType: string } =>
              x != null
          )
          if (attachments.length === 0) {
            message.error('图片上传失败')
            return
          }
          const verb = isAddingFlow ? '添加' : '替换为'
          const action = isBatch
            ? `${verb}这些图片(共 ${attachments.length} 张,按 左→右、上→下 顺序映射;图片不足时循环重复)`
            : attachments.length === 1
              ? `${verb}这张图片`
              : `${verb}这些图片(共 ${attachments.length} 张参考图)`
          const messageText = isBatch
            ? `填 ${rangeRef} 区域的图片。要求：${action}`
            : `填 ${rangeRef} 这一格。要求：${action}`
          handleSend(
            messageText,
            [
              {
                ...targetSnapshot,
                id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
              }
            ],
            attachments,
            pickAiFillSelectionKind(targetSnapshot)
          )
        })
        return
      }

      const isBatch = quickEditTarget?.quickEditMode === 'image-batch'
      const cellRefs = isBatch
        ? effectiveCellRefsFromContent(quickEditTarget)
        : []
      const limit = isBatch ? cellRefs.length || files.length : 30
      // 已上传的占位 + 新选的不超过 limit
      const accepted: File[] = []
      const rejected: File[] = []
      setQuickEditImages((prev) => {
        const remaining = Math.max(0, limit - prev.length)
        files.forEach((f, i) => {
          if (i < remaining) accepted.push(f)
          else rejected.push(f)
        })
        const next = [...prev]
        accepted.forEach((file) => {
          const mediaType = file.type || 'image/*'
          if (!mediaType.startsWith('image/')) {
            next.push({
              fileName: file.name,
              mediaType,
              status: 'error',
              error: '不是图片文件'
            })
            return
          }
          const previewUrl = URL.createObjectURL(file)
          const placeholder: QuickEditImageAttachment = {
            fileName: file.name,
            mediaType,
            previewUrl,
            status: 'uploading'
          }
          next.push(placeholder)
          xheyAi
            .uploadAttachment(file)
            .then((r) => {
              setQuickEditImages((cur) =>
                cur.map((it) => {
                  if (it !== placeholder && it.previewUrl !== previewUrl)
                    return it
                  if (it.previewUrl?.startsWith('blob:')) {
                    URL.revokeObjectURL(it.previewUrl)
                  }
                  return {
                    fileName: r.fileName || file.name,
                    fileUrl: r.fileUrl,
                    mediaType,
                    previewUrl: r.fileUrl,
                    status: 'done'
                  }
                })
              )
            })
            .catch((err) => {
              setQuickEditImages((cur) =>
                cur.map((it) =>
                  it.previewUrl === previewUrl
                    ? {
                        ...it,
                        status: 'error',
                        error: String((err && err.message) || err || '上传失败')
                      }
                    : it
                )
              )
            })
        })
        return next
      })
      if (rejected.length > 0) {
        const hint = isBatch
          ? `选区只有 ${limit} 个单元格,只接受了前 ${accepted.length} 张图片`
          : '最多可添加30张图片'
        message.warning(hint)
      }
    },
    [quickEditTarget]
  )

  const handleQuickEditConfirm = useCallback(() => {
    if (!quickEditTarget) return
    if (quickEditImages.some((it) => it.status === 'uploading')) return

    const value = quickEditValue.trim()
    const isImageCell = isImageCellContent(quickEditTarget)
    const isBatch = isImageBatchContent(quickEditTarget)
    if (!isImageCell && !value) return

    // 按"添加" vs "修改"区分 prompt 模板:
    //   - cellState='empty' / 'all-empty' → 添加(浮窗"添加图片/添加文字"触发,或双击空 cell)
    //   - 其他                              → 修改(双击有内容的 cell,或浮窗"换图/修改")
    const isAdd =
      quickEditTarget.cellState === 'empty' ||
      quickEditTarget.cellState === 'all-empty'
    const ref =
      quickEditTarget.rangePayload?.cells[0]?.[0]?.ref ||
      quickEditTarget.cellRef ||
      '这一格'
    const opening = isAdd ? `填 ${ref} 这一格` : `改 ${ref} 这一格`

    let messageText: string
    let attachments: Array<{
      url: string
      name: string
      mediaType: string
    }> = []

    if (isBatch) {
      // 批量图片:N 张图按"左→右、上→下"顺序映射到 N 格,不足循环
      const range =
        quickEditTarget.rangePayload?.range ||
        quickEditTarget.cellRef ||
        '这片区域'
      const cellRefs = effectiveCellRefsFromContent(quickEditTarget)
      const cellCount = cellRefs.length || 1
      const valid = quickEditImages.filter(
        (it) => it.status === 'done' && it.fileUrl
      )
      if (valid.length === 0 && !value) return
      attachments = valid.map((it) => ({
        url: it.fileUrl as string,
        name: it.fileName,
        mediaType: it.mediaType
      }))
      const verb = isAdd ? '填入' : '替换为'
      const action =
        valid.length === 0
          ? `${verb}用户描述的图片`
          : valid.length >= cellCount
            ? `按顺序(从左到右,从上到下)${verb}这 ${valid.length} 张图片`
            : `按顺序(从左到右,从上到下)${verb}这 ${valid.length} 张图片;图片数少于单元格数,循环重复使用直至填满 ${cellCount} 格`
      const baseOpen = isAdd ? `填 ${range} 区域` : `改 ${range} 区域`
      if (valid.length > 0 && value) {
        messageText = `${baseOpen}。要求：${action};${value}`
      } else if (valid.length > 0) {
        messageText = `${baseOpen}。要求：${action}`
      } else {
        messageText = `${baseOpen}。要求：${value}`
      }
    } else if (quickEditTarget.source === 'image-selection') {
      // 非表格图片选区"换图"(浮窗 replace-image-selection 触发):
      // 没有 cell ref / range,目标是 docx 段落里的 <img>(1 张或多张拖选)。
      // 原图 src 列表通过 quote 块由 formatSelectedAsQuote 传给后端,不写
      // 进 user message text(URL 太长,挤输入框 + chat 历史);用 ViewPath
      // 在 text 里指明定位(body/p[N]/img[M]),agent 后端按路径走 docx tree。
      // ⚠ 必须在 isImageCell 之前判断:image-selection 的 quickEditMode='image' 会让
      // isImageCellContent 返回 true,落到 cell 分支后 opening("填/改 这一格") 不对。
      const oldN = quickEditTarget.imageSrcs?.length || 0
      const oldPaths = (quickEditTarget.imageViewPaths || []).filter(Boolean)
      const oldDesc =
        oldN > 1
          ? oldPaths.length === oldN
            ? `以下 ${oldN} 张图片(${oldPaths.join(', ')})`
            : `这 ${oldN} 张图片`
          : oldPaths.length === 1
            ? `图片 ${oldPaths[0]}`
            : '这张图片'
      const valid = quickEditImages.filter(
        (it) => it.status === 'done' && it.fileUrl
      )
      if (valid.length === 0 && !value) return
      attachments = valid.map((it) => ({
        url: it.fileUrl as string,
        name: it.fileName,
        mediaType: it.mediaType
      }))
      const n = valid.length
      // 新图 vs 旧图数量关系决定换图语义:
      //   - 1 → 1: "把图片换成这张"
      //   - N → 1: "用这张图把这 N 张全替换" (一对多复制)
      //   - 1 → N: "参考这些图(共 N 张)选一张最合适的替换"
      //   - N → M: "按顺序对应替换(不足循环 / 多余截断)"
      let action = ''
      if (n === 1 && oldN <= 1) action = '把图片换成这张'
      else if (n === 1 && oldN > 1) action = `用这张图把这 ${oldN} 张全替换`
      else if (n > 1 && oldN <= 1)
        action = `参考这些图片(共 ${n} 张)选一张最合适的替换`
      else if (n > 1 && oldN > 1)
        action = `按顺序对应替换(共 ${n} 张新图对应 ${oldN} 张旧图,不足循环)`
      if (n > 0 && value) {
        messageText = `把文档里${oldDesc}换。要求:${action};${value}`
      } else if (n > 0) {
        messageText = `把文档里${oldDesc}。要求:${action}`
      } else {
        messageText = `把文档里${oldDesc}。要求:${value}`
      }
    } else if (isImageCell) {
      // 单格 image 模式现在也支持多张图(quickEditImages):
      //   - 1 张:"添加这张图片" / "把图片换成这张"
      //   - 多张:"添加这些图片" / "把图片换成下面其中一张(参考这些图)"
      const valid = quickEditImages.filter(
        (it) => it.status === 'done' && it.fileUrl
      )
      const n = valid.length
      let action: string
      if (n === 0) action = ''
      else if (n === 1) action = isAdd ? '添加这张图片' : '把图片换成这张'
      else
        action = isAdd
          ? `添加这些图片(共 ${n} 张参考图)`
          : `参考这些图片(共 ${n} 张)选一张最合适的替换`
      if (n > 0 && value) {
        messageText = `${opening}。要求：${action};${value}`
      } else if (n > 0) {
        messageText = `${opening}。要求：${action}`
      } else {
        // 没上传图但有文字描述:把文字当作"要求"传给 AI
        messageText = `${opening}。要求：${value}`
      }
      attachments = valid.map((it) => ({
        url: it.fileUrl as string,
        name: it.fileName,
        mediaType: it.mediaType
      }))
    } else if (quickEditTarget.source === 'text-selection') {
      // 文字选区"修改"(浮窗 modify-text-selection 触发):没有 cell ref,
      // 也没有 range,目标是 docx 段落里的一段具体文字。把原文带过去让
      // AI 在文档里定位替换。
      const original = quickEditTarget.text || ''
      messageText = `把文档中的这段内容改为"${value}"。原文："${original}"`
    } else {
      // 文字模式:添加 → "填入 xxx";修改 → "改为 xxx"
      // 多 cell all-empty/all-text 也走这里,opening 是首 cell ref + 区域上下文
      const action = isAdd ? `填入${value}` : `改为${value}`
      const range = quickEditTarget.rangePayload?.range
      const isMulti = quickEditTarget.source === 'cell-range' && range
      const realOpening =
        isMulti && isAdd
          ? `填 ${range} 区域`
          : isMulti
            ? `改 ${range} 区域`
            : opening
      messageText = `${realOpening}，要求：${action}`
    }

    handleSend(
      messageText,
      [
        {
          ...quickEditTarget,
          id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
        }
      ],
      attachments,
      pickAiFillSelectionKind(quickEditTarget)
    )
    closeQuickEditModal()
  }, [
    quickEditImages,
    quickEditTarget,
    quickEditValue,
    handleSend,
    closeQuickEditModal
  ])

  const quickEditDisplayRef = useMemo(() => {
    if (!quickEditTarget) return ''
    return (
      quickEditTarget.rangePayload?.cells[0]?.[0]?.ref ||
      quickEditTarget.cellRef ||
      ''
    )
  }, [quickEditTarget])

  /** 选区是否跨多格(行 > 1 或列 > 1):用于标题"X 这一格" vs "X:Y 区域"切换 */
  const quickEditIsRangeMulti = useMemo(() => {
    const p = quickEditTarget?.rangePayload
    if (!p || !p.range) return false
    const [start, end] = p.range.split(':')
    return !!(start && end && start !== end)
  }, [quickEditTarget])

  /** 跨多格时的范围字符串("F2:F5");单格时为空,由调用方走 quickEditDisplayRef */
  const quickEditRangeRef = useMemo(() => {
    return quickEditTarget?.rangePayload?.range || ''
  }, [quickEditTarget])

  const quickEditIsImage = useMemo(
    () => isImageCellContent(quickEditTarget),
    [quickEditTarget]
  )
  const quickEditIsBatch = useMemo(
    () => isImageBatchContent(quickEditTarget),
    [quickEditTarget]
  )
  // 简洁两卡片(团队相册 / 本地上传)UI 的触发条件:
  //   - 必须是"图片模式"(quickEditIsImage)—— 否则"添加文字"也会进这里
  //   - 空 cell 添加图片(cellState='empty' / 'all-empty')
  //   - 图片 cell 换图(cellState='image' / 'all-image')—— 用户期望"选完即发"
  // 共同特征:没有已选图片(quickEditImages.length === 0)
  const quickEditIsAddingPickStage = useMemo(
    () =>
      quickEditIsImage &&
      quickEditImages.length === 0 &&
      (quickEditTarget?.cellState === 'empty' ||
        quickEditTarget?.cellState === 'all-empty' ||
        quickEditTarget?.cellState === 'image' ||
        quickEditTarget?.cellState === 'all-image'),
    [quickEditIsImage, quickEditImages.length, quickEditTarget]
  )

  // 换图选图半屏进入埋点:isAddingPickStage 切到 true 时上报
  // string1=range / number1=cells(rangePayload 里的总格数,合并 part 也算)
  const lastImagePickerEnteredRef = useRef(false)
  useEffect(() => {
    if (quickEditIsAddingPickStage && !lastImagePickerEnteredRef.current) {
      const p = quickEditTarget?.rangePayload
      const cells = p ? p.cells.reduce((n, row) => n + row.length, 0) : 1
      trackEnter('imagePicker', {
        requirement_id: 1103,
        track_sign: 'aai.b8138.pageview.ai_enter_web_ai_image_picker',
        sessionID: readSessionId(),
        string1: quickEditRangeRef || quickEditDisplayRef || '',
        number1: cells
      })
    }
    lastImagePickerEnteredRef.current = quickEditIsAddingPickStage
  }, [
    quickEditIsAddingPickStage,
    quickEditTarget,
    quickEditRangeRef,
    quickEditDisplayRef
  ])

  // 双栏入口(左 textarea 内容输入 + 右选图卡片):双击空白 cell 触发,
  // 没有明确 mode hint(浮窗触发会带 quickEditMode='image'|'text'),
  // 单 cell。文字描述和选图二选一发送即可。
  const quickEditIsDualEntry = useMemo(
    () =>
      !quickEditTarget?.quickEditMode &&
      quickEditImages.length === 0 &&
      (quickEditTarget?.cellState === 'empty' ||
        quickEditTarget?.cellState === 'all-empty'),
    [quickEditTarget, quickEditImages.length]
  )
  // 批量模式:有效 cell ref 列表(跳过合并占位),供 grid 渲染 / input 端拦截
  const quickEditBatchCellRefs = useMemo(
    () => effectiveCellRefsFromContent(quickEditTarget),
    [quickEditTarget]
  )

  // ─── quick-edit popover:fixed 卡片 + 锚定到双击 cell ────────
  const popoverRef = useRef<HTMLDivElement | null>(null)
  // 真实测量到的 popover 高度:估算值(480/280)在某些场景不准导致 placement 翻转错
  // 误判,measured 后用真实高度做二次校正
  const [measuredPopoverH, setMeasuredPopoverH] = useState<number | null>(null)

  // popover 位置 + 箭头几何:
  //   - 优先放 cell 下方,空间不够翻转到上方;水平居中 + 边界 clamp
  //   - arrow 朝向跟 popover 相对 cell 的位置反过来(popover 在下→箭头朝上)
  //   - 箭头水平位置对齐 cell 中心,clamp 到 popover 内侧 20-W-20 之间
  const popoverGeometry = useMemo<{
    style: React.CSSProperties
    arrow: { placement: 'top' | 'bottom'; left: number } | null
  }>(() => {
    // AddingPickStage(空 cell 选图):UI 是两张简洁卡片,不需要换图模式那么宽
    // 整体缩到约一半宽度,让 popover 视觉更轻、跟选区对齐感更强
    // DualEntry(双击空白):左 textarea + 右两张图源卡片,需要更宽
    const W = quickEditIsAddingPickStage
      ? quickEditIsBatch
        ? 440
        : 400
      : quickEditIsDualEntry
        ? 680
        : quickEditIsBatch
          ? 820
          : quickEditIsImage
            ? 760
            : 520
    const EDGE = 12
    const ARROW_GAP = 10 // 箭头到 cell 的间距(同时是箭头突起高度)
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800

    // 没 anchor:fallback 居中,不显示箭头
    // 用数值 left/top 居中(不用 transform: translate(-50%, -50%)),
    // 否则会跟 CSS keyframes 的 `transform: scale(0.94)` 冲突 —
    // 动画期间 translate 被 scale 覆盖,popover 左上角飞到 (50%, 50%),
    // 视觉上像"先在右下,再到中间"的抖动
    if (!quickEditAnchor) {
      const estH = measuredPopoverH ?? (quickEditIsImage ? 480 : 280)
      const fallbackLeft = Math.max(EDGE, (vw - W) / 2)
      const fallbackTop = Math.max(EDGE, (vh - estH) / 2)
      return {
        style: {
          position: 'fixed',
          left: fallbackLeft,
          top: fallbackTop,
          width: W,
          maxHeight: vh * 0.85,
          transformOrigin: 'center center'
        },
        arrow: null
      }
    }

    const a = quickEditAnchor
    const centerX = (a.left + a.right) / 2
    let left = centerX - W / 2
    left = Math.max(EDGE, Math.min(vw - W - EDGE, left))

    // popover 高度:优先用 measured 真实高度(measured 之前用估算,measured 之后用真实);
    // 估算 480/280 在某些场景偏大/偏小,会导致 placement 翻转误判
    const estH = measuredPopoverH ?? (quickEditIsImage ? 480 : 280)

    // 两个候选位置:cell 下方 / cell 上方
    const topBelow = a.bottom + ARROW_GAP
    const topAbove = a.top - ARROW_GAP - estH
    const belowFits = topBelow + estH <= vh - EDGE
    const aboveFits = topAbove >= EDGE

    let top: number
    let arrowPlacement: 'top' | 'bottom'

    if (belowFits) {
      // cell 下方放得下 → popover 在下方,箭头朝上指向 cell
      top = topBelow
      arrowPlacement = 'top'
    } else if (aboveFits) {
      // cell 上方放得下 → popover 在上方,箭头朝下指向 cell
      top = topAbove
      arrowPlacement = 'bottom'
    } else {
      // 两边都装不下:选空间更大的一边,popover 紧贴 cell;
      // placement 保持跟 cell 相对位置一致,箭头不再指向虚空
      const spaceBelow = vh - a.bottom - EDGE
      const spaceAbove = a.top - EDGE
      if (spaceBelow >= spaceAbove) {
        top = topBelow
        arrowPlacement = 'top'
      } else {
        top = Math.max(EDGE, topAbove)
        arrowPlacement = 'bottom'
      }
    }

    // 箭头水平位置:对齐 cell 中心,相对 popover 左边
    const arrowLeft = Math.max(20, Math.min(W - 20, centerX - left))

    // transform-origin 用中心点:动画 scale(0.94)→1 时从 popover 几何中心
    // 缩放,避免视觉上"从右上/右下角长出"的抖动。
    // 之前用箭头位置作为 origin,在 arrowLeft 偏离中心(cell 在屏幕右侧或左侧)时,
    // scale 起步会让 popover 看起来先飞到一角再回正中,被用户误判为"位置抖动"。
    const transformOrigin = 'center center'

    return {
      style: {
        position: 'fixed',
        left,
        top,
        width: W,
        maxHeight: vh * 0.85,
        transformOrigin
      },
      arrow: { placement: arrowPlacement, left: arrowLeft }
    }
  }, [
    quickEditAnchor,
    quickEditIsImage,
    quickEditIsBatch,
    quickEditIsAddingPickStage,
    quickEditIsDualEntry,
    measuredPopoverH
  ])

  // popover mount 后测真实高度,二次校正 placement(估算的 480/280 在某些场景偏差大)
  useLayoutEffect(() => {
    if (!quickEditTarget) {
      setMeasuredPopoverH(null)
      return
    }
    // 用 rAF 等动画 mount 一帧,拿稳定高度
    const raf = requestAnimationFrame(() => {
      const el = popoverRef.current
      if (!el) return
      const h = el.getBoundingClientRect().height
      // 差异 > 4px 才更新,避免抖动死循环
      setMeasuredPopoverH((prev) =>
        prev != null && Math.abs(prev - h) <= 4 ? prev : h
      )
    })
    return () => cancelAnimationFrame(raf)
    // value 变化导致 textarea autoSize 高度变,需要重测
  }, [quickEditTarget, quickEditValue, quickEditImages])

  // 点 popover 外部关闭(延迟一帧绑,避免触发本次双击的 mouseup 立即关)
  useEffect(() => {
    if (!quickEditTarget) return
    const onMouseDown = (e: MouseEvent) => {
      const el = popoverRef.current
      if (!el) return
      if (el.contains(e.target as Node)) return
      // 排除点击发生在任何浮层(团队相册 picker / dropdown / antd picker 等)上
      // 的情况;这些浮层 portal 到 document.body,不是 popover 的 descendant,
      // 不排除会让 popover 在用户操作浮层时被误关
      const target = e.target as HTMLElement | null
      if (
        target &&
        target.closest(
          [
            '.ant-modal-root',
            '.ant-modal-wrap',
            '.ant-modal-mask',
            '.ant-dropdown',
            '.ant-select-dropdown',
            '.ant-picker-dropdown',
            '.ant-calendar-picker-container',
            '.ant-cascader-menus',
            '.ant-popover',
            '.ant-tooltip'
          ].join(',')
        )
      ) {
        return
      }
      setQuickEditTarget(null)
      setQuickEditAnchor(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setQuickEditTarget(null)
        setQuickEditAnchor(null)
      }
    }
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown)
    }, 0)
    document.addEventListener('keydown', onKey)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [quickEditTarget])

  const handleAttachmentAction = useCallback(
    (action: AttachmentAction) => {
      // share-targets 是纯前端副作用（剪贴板 / mailto / Modal 提示），
      // 不需要触发 AI 新轮对话；ShareTargets 组件内部已完成所有交互
      if (action.type === 'share-targets') return
      if (
        action.type === 'export-panel' &&
        action.payload?.fromFile === true &&
        typeof action.payload?.sourceUrl === 'string' &&
        action.payload.sourceUrl
      ) {
        window.open(action.payload.sourceUrl, '_blank', 'noopener,noreferrer')
        return
      }

      let userMessage = ''
      switch (action.type) {
        case 'quick-replies':
          userMessage = action.payload?.label || action.action
          break
        case 'export-panel':
          userMessage = `导出为 ${action.payload?.format} 格式`
          break
        case 'template-save':
          // 不直接调存模板接口，改为往会话里发一条让 AI 帮用户产出"可复用技能"的请求，
          // 由 AI 基于刚才的表自动生成「技能名 / 一句话用途 / 触发词 / 执行规则」。
          userMessage = `请基于我们的沟通内容，直接生成一张可复用的技能卡片，供我下次一句话调用。不要提问，直接采用你认为最合理的默认方案输出最终结果。请包含：技能名、用途、触发词、适用场景、执行规则、
  字段填写规则、照片/内容匹配规则、异常处理规则、可调参数。目标是让我下次处理同类表格时，只需一句话触发，你就能按这套规则继续处理。`
          break
        default:
          userMessage = action.action
      }
      handleSend(userMessage, [])
    },
    [handleSend]
  )

  // ────────── 文档面板三件套 handler ──────────
  // 注: 三件套不再标记 doc 为已同步 — staleReady badge 现在只跟刷新预览挂钩.
  const handleExportDoc = useCallback(() => {
    // 导出需「高级及以上」：基础会员(初级)/非会员 → 引导升级到高级，不导出
    if (!canExportShare) {
      openExportShareUpgrade()
      return
    }
    trackClick('formFilling', 'export', {
      requirement_id: 1103,
      track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
      sessionID: readSessionId()
    })
    const url = toAbsoluteUrl(previewBaseUrl || fileUrl)
    if (!url) {
      trackResult('formFilling', 'export_doc', {
        requirement_id: 1103,
        track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
        sessionID: readSessionId(),
        actionResult: 'fail',
        errorMsg: '文件 URL 缺失'
      })
      return
    }
    // 浏览器 window.open 是同步的,没法测真实下载耗时,这里耗时记为打开
    // 新 tab 的 ~0ms,留 costTime 字段填 0 表示"已成功跳转"
    window.open(url, '_blank', 'noopener,noreferrer')
    trackResult('formFilling', 'export_doc', {
      requirement_id: 1103,
      track_sign: 'aai.b8137.click.ai_result_web_ai_form_filling',
      sessionID: readSessionId(),
      actionResult: 'success',
      costTime: 0
    })
  }, [previewBaseUrl, fileUrl, canExportShare, openExportShareUpgrade])

  // "保存模板"硬入口已下线 — 由 Agent 在填表完成的合适时机自决主动提议
  // (一会话一次), 用户回应后 LLM 自己调 register_team_skill. 前端不再提供
  // 固定按钮, 避免用户在 Agent 还没把表填完时误触.

  const [shareOpen, setShareOpen] = useState(false)
  const shareUrl = useMemo(
    () => toAbsoluteUrl(previewBaseUrl || fileUrl),
    [previewBaseUrl, fileUrl]
  )
  // ShareTargets 内部 channel key: link / email / wechat / dingtalk
  // 文档要求的 clickItem 是 copy_link / email / wechat / dingtalk(link 改名)
  const handleShareConsumed = useCallback((key: string) => {
    const clickItem = key === 'link' ? 'copy_link' : key
    trackDialogClick('formFilling', 'share_panel', clickItem, {
      requirement_id: 1103,
      track_sign: 'aai.b8137.click.ai_dialog_click_web_ai_form_filling',
      sessionID: readSessionId()
    })
    setShareOpen(false)
  }, [])

  // 「继续使用」入口: 没文件但有 skillTitle, 走 chat-only 模式 — LLM 按 SKILL.md
  // 流程主动问用户需要什么数据 (时间/关键词/上传新模板等), 用户给后 LLM 直接做完.
  // 不能走"没文件请回首页"早 return.
  if (!fileUrl && !skillTitle) {
    return (
      <div className="aiClerk-work aiClerk-work--empty">
        <p>没有要处理的文件，请回到首页上传</p>
        <button onClick={() => navigate('/ai/clerk')}>← 回到首页</button>
      </div>
    )
  }

  return (
    <div className="aiClerk-work">
      <div className="aiClerk-work__body">
        <div className="aiClerk-work__preview-col">
          <section className="aiClerk-work__preview">
            <div className="aiClerk-work__preview-header">
              <span className="aiClerk-work__preview-file" title={fileName}>
                <FileTypeIcon
                  name={pickPreviewFileIcon(fileName).type}
                  className="aiClerk-work__preview-file-icon"
                  style={{ color: pickPreviewFileIcon(fileName).color }}
                />
                {/* 文件名超长时中间省略:head 走 ellipsis,tail 永远显示后 8 字符
                    (一般包含 ".xlsx" 等后缀),让用户始终看到文件类型 */}
                {fileName.length > 8 ? (
                  <>
                    <span className="aiClerk-work__preview-file-head">
                      {fileName.slice(0, -8)}
                    </span>
                    <span className="aiClerk-work__preview-file-tail">
                      {fileName.slice(-8)}
                    </span>
                  </>
                ) : (
                  <span className="aiClerk-work__preview-file-tail">
                    {fileName}
                  </span>
                )}
              </span>
              <div className="aiClerk-work__preview-actions">
                {hasEditableDocTool && (
                  <DocStatusBadge state={docActionState} />
                )}
                <button
                  type="button"
                  className={`aiClerk-work__preview-refresh${
                    previewLoadingState.busy ? ' is-busy' : ''
                  }`}
                  onClick={triggerPreviewRefresh}
                  disabled={previewLoadingState.busy}
                  title="刷新预览"
                  aria-label="刷新预览"
                  style={
                    {
                      // 进度环用 conic-gradient,百分比转角度
                      ['--aiClerk-preview-progress-deg' as any]: `${
                        Math.max(
                          0,
                          Math.min(
                            1,
                            previewLoadingState.progress == null
                              ? 0
                              : previewLoadingState.progress
                          )
                        ) * 360
                      }deg`
                    } as React.CSSProperties
                  }
                >
                  <span
                    className={`aiClerk-work__preview-refresh-icon${
                      previewLoadingState.busy ? ' is-spinning' : ''
                    }`}
                  >
                    <SyncOutlined />
                  </span>
                </button>
                {!isDocxPreview && (
                  <Select
                    size="small"
                    value={previewZoom}
                    onChange={(v: number) => setPreviewZoom(v)}
                    dropdownMatchSelectWidth={false}
                    className="aiClerk-work__preview-zoom-select"
                  >
                    <Select.Option value={0.5}>50%</Select.Option>
                    <Select.Option value={0.7}>70%</Select.Option>
                    <Select.Option value={1}>100%</Select.Option>
                    <Select.Option value={1.15}>115%</Select.Option>
                  </Select>
                )}
              </div>
            </div>
            <DocumentPreview
              source={previewSource}
              onContentSelected={handleContentSelected}
              onQuickAction={handleQuickAction}
              onCellQuickEdit={handleCellQuickEdit}
              zoom={previewZoom}
              onLoadingStateChange={setPreviewLoadingState}
              editing={docActionState.phase === 'editing'}
              syncing={maskHold && docActionState.phase !== 'editing'}
              onDismissSync={handleDismissSync}
            />
          </section>
          {/* 底部操作条:再填一份 + 三件套 */}
          <div className="aiClerk-work__preview-footer">
            <Tooltip
              placement="bottom"
              title="返回首页,上传新文档或继续别的技能"
            >
              <button
                type="button"
                className="aiClerk-work__refill-btn"
                onClick={() => {
                  trackClick('formFilling', 'refill', {
                    requirement_id: 1103,
                    track_sign: 'aai.b8137.click.ai_click_web_ai_form_filling',
                    sessionID: readSessionId()
                  })
                  try {
                    window.localStorage.removeItem(CONV_KEY)
                  } catch {
                    /* 忽略 */
                  }
                  navigate('/ai/clerk')
                }}
              >
                <LeftOutlined className="aiClerk-work__refill-btn-icon" />
                <span>再填一份</span>
              </button>
            </Tooltip>
            {hasEditableDocTool && (
              <DocTrioActions
                state={docActionState}
                onExport={handleExportDoc}
                vipLocked={!canExportShare}
                shareUrl={shareUrl}
                fileName={fileName}
                shareOpen={shareOpen}
                onShareToggle={() => {
                  // toggle: 关 → 开 = 点 share 入口;开 → 关 = 主动收起
                  const opening = !shareOpen
                  // 分享需「高级及以上」：基础会员(初级)/非会员 → 引导升级，不打开分享面板
                  if (opening && !canExportShare) {
                    openExportShareUpgrade()
                    return
                  }
                  if (opening) {
                    trackClick('formFilling', 'share', {
                      requirement_id: 1103,
                      track_sign:
                        'aai.b8137.click.ai_click_web_ai_form_filling',
                      sessionID: readSessionId()
                    })
                    trackDialogShow('formFilling', 'share_panel', {
                      requirement_id: 1103,
                      track_sign:
                        'aai.b8137.pageview.ai_dialog_show_web_ai_form_filling',
                      sessionID: readSessionId()
                    })
                  } else {
                    trackDialogClick('formFilling', 'share_panel', 'close', {
                      requirement_id: 1103,
                      track_sign:
                        'aai.b8137.click.ai_dialog_click_web_ai_form_filling',
                      sessionID: readSessionId()
                    })
                  }
                  setShareOpen((v) => !v)
                }}
                onShareConsumed={handleShareConsumed}
              />
            )}
          </div>
        </div>

        <aside className="aiClerk-work__chat">
          <ChatPanel
            messages={displayMessages}
            quotes={quotes}
            onRemoveQuote={handleRemoveQuote}
            sending={sending}
            onSend={handleSend}
            onAttachmentAction={handleAttachmentAction}
            onAbort={abort}
            prefillInput={prefillInput}
            onUserAskSubmit={handleUserAskSubmit}
            sessionId={resumeSessionId}
            conversationId={resumeSessionId}
            pendingQueue={pendingQueue.map((p) => ({ id: p.id, text: p.text }))}
            onCancelPending={handleCancelPending}
            composerFooter={
              credits &&
              credits.total_quota != null &&
              credits.consumed != null ? (
                <div
                  className="aiClerk-work__points-entry-hover"
                  onMouseEnter={() => setPointsCardVisible(true)}
                  onMouseLeave={() => setPointsCardVisible(false)}
                >
                  {pointsCardVisible && (
                    <div className="aiClerk-work__points-card-popover">
                      <PointsStatusCard
                        vipKind={vipKind}
                        total={credits.total_quota}
                        used={credits.consumed}
                        perQueryCost={SINGLE_QUERY_COST}
                        onExpose={() => {
                          // hover 出来 → 弹窗曝光 credit_hover(图2)，带 number1 + string2
                          trackDialogShow('formFilling', 'credit_hover', {
                            requirement_id: 1103,
                            track_sign: TRACK_SIGN_DIALOG_SHOW,
                            sessionID: readSessionId(),
                            ...buildCreditTrackingExtra()
                          })
                        }}
                        onCtaClick={handlePointsCta}
                        onContactClick={() => {
                          trackDialogClick(
                            'formFilling',
                            'credit_hover',
                            'contact_service',
                            {
                              requirement_id: 1103,
                              track_sign: TRACK_SIGN_DIALOG_CLICK,
                              sessionID: readSessionId()
                            }
                          )
                          agentCustomerDrawer.open({ from: 'aiClerk_points' })
                        }}
                      />
                    </div>
                  )}
                  <PointsEntryChip
                    vipKind={vipKind}
                    total={credits.total_quota}
                    used={credits.consumed}
                    perQueryCost={SINGLE_QUERY_COST}
                    onCtaClick={handlePointsCta}
                  />
                </div>
              ) : null
            }
          />
        </aside>
      </div>
      {quickEditTarget &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className={`aiClerk-work__quick-edit-popover${
              quickEditIsImage ? ' is-image' : ' is-text'
            }${quickEditIsBatch ? ' is-batch' : ''}${
              quickEditIsAddingPickStage ? ' is-add' : ''
            }${quickEditIsDualEntry ? ' is-dual' : ''}${
              popoverGeometry.arrow
                ? ` is-arrow-${popoverGeometry.arrow.placement}`
                : ''
            }`}
            style={popoverGeometry.style}
          >
            {popoverGeometry.arrow && (
              <span
                className="aiClerk-work__quick-edit-popover-arrow"
                style={{ left: popoverGeometry.arrow.left }}
                aria-hidden
              />
            )}
            <div className="aiClerk-work__quick-edit-popover-header">
              <div className="aiClerk-work__quick-edit-title">
                <span className="aiClerk-work__quick-edit-title-icon">
                  {quickEditIsImage ? <PictureOutlined /> : <EditOutlined />}
                </span>
                <span className="aiClerk-work__quick-edit-title-text">
                  {quickEditIsBatch ? (
                    <>
                      批量
                      {quickEditTarget?.cellState === 'all-image'
                        ? '换图'
                        : '添加图片'}
                      <span className="aiClerk-work__quick-edit-title-ref">
                        {quickEditTarget?.rangePayload?.range || ''}
                      </span>
                      ({quickEditBatchCellRefs.length} 格)
                    </>
                  ) : quickEditIsAddingPickStage ? (
                    // 单格未选阶段:空 cell 显示"添加图片",图片 cell 显示"换图"
                    quickEditTarget?.cellState === 'image' ? (
                      <>换图</>
                    ) : (
                      <>添加图片</>
                    )
                  ) : quickEditIsDualEntry ? (
                    // 双栏入口:双击空白 cell 触发
                    <>
                      填入
                      {quickEditDisplayRef && (
                        <span className="aiClerk-work__quick-edit-title-ref">
                          {quickEditDisplayRef}
                        </span>
                      )}
                      这一格
                    </>
                  ) : quickEditIsRangeMulti ? (
                    // 多选空 cell / 多选文字 cell → "修改 F2:F5 区域"
                    <>
                      修改
                      <span className="aiClerk-work__quick-edit-title-ref">
                        {quickEditRangeRef}
                      </span>
                      区域
                    </>
                  ) : (
                    <>
                      修改
                      {quickEditDisplayRef && (
                        <span className="aiClerk-work__quick-edit-title-ref">
                          {quickEditDisplayRef}
                        </span>
                      )}
                      这一格
                    </>
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  // 仅"添加图片未选"阶段才属于 image picker 半屏的 close 事件
                  if (quickEditIsAddingPickStage) {
                    trackClick('imagePicker', 'close', {
                      requirement_id: 1103,
                      track_sign:
                        'aai.b8138.click.ai_click_web_ai_image_picker',
                      sessionID: readSessionId()
                    })
                  }
                  closeQuickEditModal()
                }}
                className="aiClerk-work__quick-edit-popover-close"
                aria-label="关闭"
              >
                <CloseOutlined />
              </button>
            </div>
            <div className="aiClerk-work__quick-edit-popover-body">
              {quickEditIsDualEntry ? (
                // 双栏入口:双击空白 cell 触发(无 mode hint)。左侧 textarea 输入文字,
                // 右侧两张图源卡片选图。用户文字 / 选图二选一即可发送。
                <div className="aiClerk-work__quick-edit-body aiClerk-work__quick-edit-body--dual">
                  <input
                    ref={quickEditImagesInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="aiClerk-work__quick-edit-file-input"
                    onChange={handleQuickEditImagesSelected}
                  />
                  <div className="aiClerk-work__quick-edit-dual-left">
                    <div className="aiClerk-work__quick-edit-dual-label">
                      输入内容
                    </div>
                    <TextArea
                      ref={quickEditTextAreaRef}
                      value={quickEditValue}
                      placeholder="请输入要填入的内容"
                      onChange={(e) => setQuickEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault()
                          handleQuickEditConfirm()
                        }
                      }}
                      autoFocus
                      autoSize={{ minRows: 4, maxRows: 8 }}
                    />
                  </div>
                  <div className="aiClerk-work__quick-edit-dual-divider">
                    <span>或</span>
                  </div>
                  <div className="aiClerk-work__quick-edit-dual-right">
                    <div className="aiClerk-work__quick-edit-dual-label">
                      选择图片
                    </div>
                    <button
                      type="button"
                      className="aiClerk-work__quick-edit-source-card aiClerk-work__quick-edit-source-card--compact"
                      onClick={handleOpenTeamPicker}
                    >
                      <span className="aiClerk-work__quick-edit-source-icon aiClerk-work__quick-edit-source-icon--team">
                        <BookFilled />
                      </span>
                      <span className="aiClerk-work__quick-edit-source-body">
                        <span className="aiClerk-work__quick-edit-source-title">
                          团队相册
                        </span>
                        <span className="aiClerk-work__quick-edit-source-desc">
                          从团队照片库找
                        </span>
                      </span>
                      <span className="aiClerk-work__quick-edit-source-arrow">
                        <RightOutlined />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="aiClerk-work__quick-edit-source-card aiClerk-work__quick-edit-source-card--compact"
                      onClick={handlePickQuickEditImages}
                    >
                      <span className="aiClerk-work__quick-edit-source-icon aiClerk-work__quick-edit-source-icon--local">
                        <FolderFilled />
                      </span>
                      <span className="aiClerk-work__quick-edit-source-body">
                        <span className="aiClerk-work__quick-edit-source-title">
                          本地上传
                        </span>
                        <span className="aiClerk-work__quick-edit-source-desc">
                          从电脑选择文件
                        </span>
                      </span>
                      <span className="aiClerk-work__quick-edit-source-arrow">
                        <RightOutlined />
                      </span>
                    </button>
                  </div>
                </div>
              ) : quickEditIsAddingPickStage ? (
                // "添加图片"未选阶段(单格 cellState=empty / 批量 all-empty + 0 张图):
                // 简洁两卡片,无 AI 文字框 / OR / batch grid 占位
                <div className="aiClerk-work__quick-edit-body aiClerk-work__quick-edit-body--add">
                  <input
                    ref={quickEditImagesInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="aiClerk-work__quick-edit-file-input"
                    onChange={handleQuickEditImagesSelected}
                  />
                  <button
                    type="button"
                    className="aiClerk-work__quick-edit-source-card"
                    onClick={handleOpenTeamPicker}
                  >
                    <span className="aiClerk-work__quick-edit-source-icon aiClerk-work__quick-edit-source-icon--team">
                      <BookFilled />
                    </span>
                    <span className="aiClerk-work__quick-edit-source-body">
                      <span className="aiClerk-work__quick-edit-source-title">
                        团队相册选图
                        <span className="aiClerk-work__quick-edit-source-badge">
                          <ThunderboltFilled />
                          推荐
                        </span>
                      </span>
                      <span className="aiClerk-work__quick-edit-source-desc">
                        从今日水印相机团队照片库找
                      </span>
                      <span className="aiClerk-work__quick-edit-source-sub">
                        可按时间 / 地点 / 拍摄人 / 关键词筛选
                      </span>
                    </span>
                    <span className="aiClerk-work__quick-edit-source-arrow">
                      <RightOutlined />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="aiClerk-work__quick-edit-source-card"
                    onClick={handlePickQuickEditImages}
                  >
                    <span className="aiClerk-work__quick-edit-source-icon aiClerk-work__quick-edit-source-icon--local">
                      <FolderFilled />
                    </span>
                    <span className="aiClerk-work__quick-edit-source-body">
                      <span className="aiClerk-work__quick-edit-source-title">
                        本地上传
                      </span>
                      <span className="aiClerk-work__quick-edit-source-desc">
                        从电脑选择文件,逐张挑选
                      </span>
                      <span className="aiClerk-work__quick-edit-source-sub">
                        支持 JPG / PNG · 单张 ≤ 10 MB
                      </span>
                    </span>
                    <span className="aiClerk-work__quick-edit-source-arrow">
                      <RightOutlined />
                    </span>
                  </button>
                </div>
              ) : quickEditIsBatch ? (
                <div className="aiClerk-work__quick-edit-body aiClerk-work__quick-edit-body--batch">
                  {/* 上:文字描述意图(选填) */}
                  <div className="aiClerk-work__quick-edit-option">
                    <div className="aiClerk-work__quick-edit-option-head">
                      <span className="aiClerk-work__quick-edit-option-badge aiClerk-work__quick-edit-option-badge--ai">
                        <ThunderboltFilled />
                      </span>
                      <div className="aiClerk-work__quick-edit-option-head-text">
                        <div className="aiClerk-work__quick-edit-option-title">
                          补充说明(选填)
                        </div>
                        <div className="aiClerk-work__quick-edit-option-desc">
                          描述图片用途或要求,可不填,直接按顺序映射
                        </div>
                      </div>
                    </div>
                    <TextArea
                      ref={quickEditTextAreaRef}
                      value={quickEditValue}
                      placeholder="比如:这些是钢筋绑扎工序照,按时间从早到晚排序"
                      onChange={(e) => setQuickEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault()
                          handleQuickEditConfirm()
                        }
                      }}
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                  </div>

                  {/* 下:上传图片 + N 格 grid 预览 */}
                  <div className="aiClerk-work__quick-edit-option">
                    <div className="aiClerk-work__quick-edit-option-head">
                      <span className="aiClerk-work__quick-edit-option-badge aiClerk-work__quick-edit-option-badge--upload">
                        <PictureFilled />
                      </span>
                      <div className="aiClerk-work__quick-edit-option-head-text">
                        <div className="aiClerk-work__quick-edit-option-title">
                          上传图片(最多 {quickEditBatchCellRefs.length} 张)
                        </div>
                        <div className="aiClerk-work__quick-edit-option-desc">
                          按“左 → 右、上 → 下”映射;图片不足时循环重复
                        </div>
                      </div>
                    </div>
                    <input
                      ref={quickEditImagesInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="aiClerk-work__quick-edit-file-input"
                      onChange={handleQuickEditImagesSelected}
                    />
                    <div className="aiClerk-work__quick-edit-batch-grid">
                      {quickEditBatchCellRefs.map((ref, idx) => {
                        // 循环映射:idx >= 上传数时取模回头
                        const upCount = quickEditImages.length
                        const item =
                          upCount > 0 ? quickEditImages[idx % upCount] : null
                        const isLooped = upCount > 0 && idx >= upCount
                        return (
                          <div
                            key={ref}
                            className={`aiClerk-work__quick-edit-batch-cell${
                              item ? ' is-filled' : ''
                            }${isLooped ? ' is-looped' : ''}`}
                          >
                            <span className="aiClerk-work__quick-edit-batch-ref">
                              {ref}
                            </span>
                            {item ? (
                              <>
                                {item.previewUrl && (
                                  <img
                                    src={item.previewUrl}
                                    alt={item.fileName}
                                    className="aiClerk-work__quick-edit-batch-img"
                                  />
                                )}
                                {item.status === 'uploading' && (
                                  <div className="aiClerk-work__quick-edit-batch-mask">
                                    <LoadingOutlined />
                                  </div>
                                )}
                                {item.status === 'error' && (
                                  <div
                                    className="aiClerk-work__quick-edit-batch-mask is-error"
                                    title={item.error}
                                  >
                                    <ExclamationCircleOutlined />
                                  </div>
                                )}
                                {isLooped && (
                                  <span className="aiClerk-work__quick-edit-batch-loop">
                                    循环
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="aiClerk-work__quick-edit-batch-empty">
                                空
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div className="aiClerk-work__quick-edit-batch-actions">
                      <Button
                        size="small"
                        onClick={handleOpenTeamPicker}
                        disabled={
                          quickEditImages.length >=
                          quickEditBatchCellRefs.length
                        }
                      >
                        <ThunderboltFilled />
                        团队相册
                      </Button>
                      <Button
                        size="small"
                        onClick={handlePickQuickEditImages}
                        disabled={
                          quickEditImages.length >=
                          quickEditBatchCellRefs.length
                        }
                      >
                        <PlusOutlined />
                        本地上传
                      </Button>
                      <span className="aiClerk-work__quick-edit-batch-counter">
                        已选 {quickEditImages.length} /{' '}
                        {quickEditBatchCellRefs.length}
                      </span>
                      {quickEditImages.length > 0 && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => {
                            quickEditImages.forEach((it) => {
                              if (it.previewUrl?.startsWith('blob:')) {
                                URL.revokeObjectURL(it.previewUrl)
                              }
                            })
                            setQuickEditImages([])
                            if (quickEditImagesInputRef.current) {
                              quickEditImagesInputRef.current.value = ''
                            }
                          }}
                        >
                          清空
                        </Button>
                      )}
                    </div>
                    {quickEditImages.length > 0 &&
                      quickEditImages.length <
                        quickEditBatchCellRefs.length && (
                        <div className="aiClerk-work__quick-edit-status">
                          图片数 ({quickEditImages.length}) 少于单元格数 (
                          {quickEditBatchCellRefs.length}
                          ),将循环重复填入
                        </div>
                      )}
                  </div>
                </div>
              ) : quickEditIsImage ? (
                <div className="aiClerk-work__quick-edit-body aiClerk-work__quick-edit-body--image">
                  {/* 左:让 AI 来改 — 用文字描述意图,AI 处理 */}
                  <div className="aiClerk-work__quick-edit-option">
                    <div className="aiClerk-work__quick-edit-option-head">
                      <span className="aiClerk-work__quick-edit-option-badge aiClerk-work__quick-edit-option-badge--ai">
                        <ThunderboltFilled />
                      </span>
                      <div className="aiClerk-work__quick-edit-option-head-text">
                        <div className="aiClerk-work__quick-edit-option-title">
                          让 AI 来改
                        </div>
                        <div className="aiClerk-work__quick-edit-option-desc">
                          用文字描述你想怎么改
                        </div>
                      </div>
                    </div>
                    <TextArea
                      ref={quickEditTextAreaRef}
                      value={quickEditValue}
                      placeholder="想换成什么样的图?比如:今天上午的现场 / 钢筋绑扎工序 / 更清楚的版本"
                      onChange={(e) => setQuickEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault()
                          handleQuickEditConfirm()
                        }
                      }}
                      autoFocus
                      autoSize={{ minRows: 6, maxRows: 9 }}
                    />
                  </div>

                  {/* 中间 OR 分隔(竖线 + "或" 字) */}
                  <div className="aiClerk-work__quick-edit-or">
                    <span className="aiClerk-work__quick-edit-or-line" />
                    <span className="aiClerk-work__quick-edit-or-text">或</span>
                    <span className="aiClerk-work__quick-edit-or-line" />
                  </div>

                  {/* 右:手动换图 — 两卡片(团队相册 / 本地上传)+ 已选预览 */}
                  <div className="aiClerk-work__quick-edit-right">
                    {/* head 跟左栏"让 AI 来改"对称:图标 + 主标题 + 副说明 */}
                    <div className="aiClerk-work__quick-edit-option-head">
                      <span className="aiClerk-work__quick-edit-option-badge aiClerk-work__quick-edit-option-badge--upload">
                        <PictureFilled />
                      </span>
                      <div className="aiClerk-work__quick-edit-option-head-text">
                        <div className="aiClerk-work__quick-edit-option-title">
                          手动换图
                        </div>
                        <div className="aiClerk-work__quick-edit-option-desc">
                          自己选择图片进行替换
                        </div>
                      </div>
                    </div>
                    <input
                      ref={quickEditImagesInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="aiClerk-work__quick-edit-file-input"
                      onChange={handleQuickEditImagesSelected}
                    />
                    {quickEditImages.length > 0 ? (
                      // 已选状态:多图缩略图 grid(支持滚动),底部"继续添加 / 团队相册 / 清空"
                      <>
                        <div className="aiClerk-work__quick-edit-img-grid">
                          {quickEditImages.map((it, idx) => (
                            <div
                              key={`${it.previewUrl || it.fileUrl}-${idx}`}
                              className="aiClerk-work__quick-edit-img-cell"
                            >
                              {it.previewUrl && (
                                <img
                                  src={it.previewUrl}
                                  alt={it.fileName}
                                  className="aiClerk-work__quick-edit-img-cell-img"
                                />
                              )}
                              {it.status === 'uploading' && (
                                <div className="aiClerk-work__quick-edit-img-cell-mask">
                                  <LoadingOutlined />
                                </div>
                              )}
                              {it.status === 'error' && (
                                <div
                                  className="aiClerk-work__quick-edit-img-cell-mask is-error"
                                  title={it.error}
                                >
                                  <ExclamationCircleOutlined />
                                </div>
                              )}
                              <button
                                type="button"
                                className="aiClerk-work__quick-edit-img-cell-remove"
                                title="移除"
                                onClick={() => removeQuickEditImageAt(idx)}
                              >
                                <CloseOutlined />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="aiClerk-work__quick-edit-img-actions">
                          <button
                            type="button"
                            className="aiClerk-work__quick-edit-img-add-btn"
                            onClick={handleOpenTeamPicker}
                            disabled={quickEditImages.length >= 30}
                          >
                            团队相册
                          </button>
                          <button
                            type="button"
                            className="aiClerk-work__quick-edit-img-add-btn"
                            onClick={handlePickQuickEditImages}
                            disabled={quickEditImages.length >= 30}
                          >
                            本地上传
                          </button>
                          <div className="aiClerk-work__quick-edit-img-status">
                            <span className="aiClerk-work__quick-edit-img-counter">
                              已选&nbsp;
                              <strong>{quickEditImages.length}</strong>
                              &nbsp;/ 30
                            </span>
                            <span className="aiClerk-work__quick-edit-img-sep">
                              ·
                            </span>
                            <button
                              type="button"
                              className="aiClerk-work__quick-edit-img-clear"
                              onClick={() => {
                                quickEditImages.forEach((it) => {
                                  if (it.previewUrl?.startsWith('blob:')) {
                                    URL.revokeObjectURL(it.previewUrl)
                                  }
                                })
                                setQuickEditImages([])
                                if (quickEditImagesInputRef.current) {
                                  quickEditImagesInputRef.current.value = ''
                                }
                              }}
                            >
                              清空
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      // 未选状态:团队相册 + 本地上传 两个卡片
                      <>
                        <button
                          type="button"
                          className="aiClerk-work__quick-edit-source-card"
                          onClick={handleOpenTeamPicker}
                        >
                          <span className="aiClerk-work__quick-edit-source-icon aiClerk-work__quick-edit-source-icon--team">
                            <BookFilled />
                          </span>
                          <span className="aiClerk-work__quick-edit-source-body">
                            <span className="aiClerk-work__quick-edit-source-title">
                              团队相册选图
                              <span className="aiClerk-work__quick-edit-source-badge">
                                <ThunderboltFilled />
                                推荐
                              </span>
                            </span>
                            <span className="aiClerk-work__quick-edit-source-desc">
                              从今日水印相机团队照片库找
                            </span>
                          </span>
                          <span className="aiClerk-work__quick-edit-source-arrow">
                            <RightOutlined />
                          </span>
                        </button>
                        <button
                          type="button"
                          className="aiClerk-work__quick-edit-source-card"
                          onClick={handlePickQuickEditImages}
                        >
                          <span className="aiClerk-work__quick-edit-source-icon aiClerk-work__quick-edit-source-icon--local">
                            <FolderFilled />
                          </span>
                          <span className="aiClerk-work__quick-edit-source-body">
                            <span className="aiClerk-work__quick-edit-source-title">
                              本地上传
                            </span>
                            <span className="aiClerk-work__quick-edit-source-desc">
                              从电脑选择文件,可多选
                            </span>
                          </span>
                          <span className="aiClerk-work__quick-edit-source-arrow">
                            <RightOutlined />
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="aiClerk-work__quick-edit-body aiClerk-work__quick-edit-body--text">
                  <div className="aiClerk-work__quick-edit-panel">
                    <div className="aiClerk-work__quick-edit-label">
                      新的内容
                    </div>
                    <div className="aiClerk-work__quick-edit-hint">
                      输入新的内容后,会自动把这个单元格的修改要求发送到右侧会话。
                      按 ⌘/Ctrl + Enter 直接发送,回车 / Shift+回车换行。
                    </div>
                    <TextArea
                      ref={quickEditTextAreaRef}
                      value={quickEditValue}
                      placeholder="请输入要修改成的内容"
                      onChange={(e) => setQuickEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        // 回车 / Shift+回车 = 换行(交给 TextArea 默认行为)
                        // ⌘/Ctrl + Enter = 直接发送
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault()
                          handleQuickEditConfirm()
                        }
                      }}
                      autoFocus
                      autoSize={{ minRows: 3, maxRows: 8 }}
                    />
                  </div>
                </div>
              )}
            </div>
            {/* "添加图片"未选阶段:用户先选图,无需 footer 发送(选完会进入下一态) */}
            {!quickEditIsAddingPickStage && (
              <div className="aiClerk-work__quick-edit-popover-footer">
                <Button
                  type="primary"
                  onClick={handleQuickEditConfirm}
                  className="aiClerk-work__quick-edit-btn aiClerk-work__quick-edit-btn--primary"
                  disabled={
                    quickEditImages.some((it) => it.status === 'uploading') ||
                    (quickEditIsImage
                      ? quickEditImages.filter((it) => it.status === 'done')
                          .length === 0 && !quickEditValue.trim()
                      : !quickEditValue.trim())
                  }
                >
                  发送
                </Button>
              </div>
            )}
          </div>,
          document.body
        )}

      {teamPickerTarget && (
        <TeamPhotoPickerModal
          visible
          maxCount={
            teamPickerTarget.mode === 'image-batch'
              ? // 批量:整片替换,maxCount = cell 数
                Math.max(
                  1,
                  effectiveCellRefsFromContent(teamPickerTarget.content).length
                )
              : // 单格 image:append 到 quickEditImages,剩余额度 = 30 - 已选数
                Math.max(
                  1,
                  30 -
                    quickEditImages.filter(
                      (it) => it.status === 'done' && it.fileUrl
                    ).length
                )
          }
          excludePhotos={
            // 单格 / 批量都从 quickEditImages 算 excludePhotos
            quickEditImages
              .filter((it) => it.status === 'done' && it.fileUrl)
              .map((it) => ({ smallURL: it.fileUrl }))
          }
          onConfirm={handleTeamPickerConfirm}
          onCancel={handleTeamPickerCancel}
        />
      )}
      {/* user_ask 独立层 ActionSheet — 替代之前 chat 流内 UserAskBlock 完整卡片渲染.
          chat 流里只留 displayAs='chip' 的轻量 chip(UserAskChip),完整交互在此.
          ⚠ P0 commit 541a76e0a 时这里 git add 漏了 work/index.tsx,
          导致 chat 流有 chip 但 ActionSheet 组件没 mount,点击 chip 没反应. */}
      <UserAskActionSheet onSubmit={handleUserAskSubmit} />
      <PointsPackModal
        visible={pointsPackVisible}
        onClose={() => setPointsPackVisible(false)}
        source="aiFormFillerBuyCredits"
        onPaymentSuccess={() => {
          setPointsPackVisible(false)
          refreshCredits()
        }}
      />
      <CreditsExhaustedModal
        visible={creditsExhaustedVisible}
        onClose={() => {
          // noVip 关闭 = 「我知道了」(图3)：弹窗点击 i_know
          if (!vipKind) {
            trackDialogClick('formFilling', 'insufficient_credits', 'i_know', {
              requirement_id: 1103,
              track_sign: TRACK_SIGN_DIALOG_CLICK,
              sessionID: readSessionId()
            })
          }
          setCreditsExhaustedVisible(false)
        }}
        available={credits?.available ?? 0}
        cost={SINGLE_QUERY_COST}
        vipKind={vipKind}
        onShow={() => {
          // 积分不足弹窗曝光 insufficient_credits(图3/4/5)，带 number1(余额) + number2(消耗)
          trackDialogShow('formFilling', 'insufficient_credits', {
            requirement_id: 1103,
            track_sign: TRACK_SIGN_DIALOG_SHOW,
            sessionID: readSessionId(),
            number1: credits?.available ?? 0,
            number2: SINGLE_QUERY_COST
          })
        }}
        onBuyPack={() => {
          // 「增购积分」按钮(vip/vvip/svip 出现)：弹窗点击 buy_credits
          trackDialogClick(
            'formFilling',
            'insufficient_credits',
            'buy_credits',
            {
              requirement_id: 1103,
              track_sign: TRACK_SIGN_DIALOG_CLICK,
              sessionID: readSessionId()
            }
          )
          setCreditsExhaustedVisible(false)
          setPointsPackVisible(true)
        }}
        onUpgrade={() => {
          // 「升级会员/开通会员」按钮：noVip → open_member(图3) / vip·vvip → upgrade_member(图4)
          const clickItem = vipKind ? 'upgrade_member' : 'open_member'
          trackDialogClick('formFilling', 'insufficient_credits', clickItem, {
            requirement_id: 1103,
            track_sign: TRACK_SIGN_DIALOG_CLICK,
            sessionID: readSessionId()
          })
          setCreditsExhaustedVisible(false)
          payment.openVipByKind(
            vipKind || CONFIG.VIP_KIND.VIP,
            'aiFormFiller',
            false,
            aiClerkPayContent({
              isVip: vipKind > 0,
              vipKind,
              usedPoints: credits?.consumed ?? 0
            })
          )
        }}
      />
    </div>
  )
}

// ───────────── 子组件：文档状态徽标（顶部 header 刷新按钮旁）─────────────
//
// 5 态视觉：
//   idle       — 不渲染（AI 还没动过，挂个 chip 反而打扰）
//   editing    — 浅紫底紫字 + 旋转 ↻ + 可选 hint（"r5-r7"）
//   ready      — 浅绿底绿字 + ✓
//   staleReady — 浅琥珀底 + ⚠ + 红色小点（提示用户："您导出的版本已过期"）
//   error      — 浅红底 + ✕ + tooltip 看错误详情

const DocStatusBadge: React.FC<{ state: DocActionState }> = ({ state }) => {
  if (state.phase === 'idle') return null

  if (state.phase === 'editing') {
    return (
      <span className="aiClerk-work__doc-badge aiClerk-work__doc-badge--editing">
        <LoadingOutlined className="aiClerk-work__doc-badge-icon is-spinning" />
        <span className="aiClerk-work__doc-badge-text">
          AI 编辑中{state.editingHint ? ` · ${state.editingHint}` : '…'}
        </span>
      </span>
    )
  }

  if (state.phase === 'ready') {
    return (
      <span className="aiClerk-work__doc-badge aiClerk-work__doc-badge--ready">
        <CheckOutlined className="aiClerk-work__doc-badge-icon" />
        <span className="aiClerk-work__doc-badge-text">当前版本</span>
      </span>
    )
  }

  if (state.phase === 'staleReady') {
    return (
      <span
        className="aiClerk-work__doc-badge aiClerk-work__doc-badge--stale"
        title="AI 已有新改动,点刷新查看最新版本"
      >
        <RedoOutlined className="aiClerk-work__doc-badge-icon" />
        <span className="aiClerk-work__doc-badge-text">已有新版本</span>
        <span className="aiClerk-work__doc-badge-dot" />
      </span>
    )
  }

  // error
  return (
    <span
      className="aiClerk-work__doc-badge aiClerk-work__doc-badge--error"
      title={state.errorMessage || '最近一次编辑失败'}
    >
      <CloseOutlined className="aiClerk-work__doc-badge-icon" />
      <span className="aiClerk-work__doc-badge-text">编辑失败</span>
    </span>
  )
}

// ───────────── 子组件：三件套按钮组（footer 右侧）─────────────
//
// 形态：1 主按钮（导出，紫底白字）+ 2 次级 icon 按钮（保存模板 / 分享）
// 状态：idle / error → 整组 disabled + tooltip；editing → disabled + 提示；
//       ready → 全亮；staleReady → 主按钮加红点 + 文案不变（视觉提示用户重导）
const DOC_TRIO_SHARE_TARGETS = [
  { key: 'link', label: '复制链接', emoji: '🔗', tone: 'neutral' as const },
  { key: 'email', label: '邮件', emoji: '✉️', tone: 'blue' as const },
  { key: 'wechat', label: '微信', emoji: '💬', tone: 'green' as const },
  { key: 'dingtalk', label: '钉钉', emoji: '🔔', tone: 'orange' as const }
]

const DocTrioActions: React.FC<{
  state: DocActionState
  onExport: () => void
  /** 基础会员(初级)/非会员：导出按钮显示皇冠 icon（导出需高级及以上，VIP 引导） */
  vipLocked?: boolean
  shareUrl: string
  fileName: string
  shareOpen: boolean
  onShareToggle: () => void
  onShareConsumed: (key: string) => void
}> = ({
  state,
  onExport,
  vipLocked,
  shareUrl,
  fileName,
  shareOpen,
  onShareToggle,
  onShareConsumed
}) => {
  const enabled = state.phase === 'ready' || state.phase === 'staleReady'
  const disabledReason =
    state.phase === 'idle'
      ? 'AI 还没动过这份文档'
      : state.phase === 'editing'
        ? 'AI 正在编辑，等编辑完成后操作'
        : state.phase === 'error'
          ? '最近一次编辑失败，先解决问题再操作'
          : ''
  const showStaleDot = state.phase === 'staleReady'

  const wrapAction = (node: React.ReactNode, tip: string) => (
    <Tooltip placement="top" title={tip}>
      <span className="aiClerk-work__doc-trio-item">{node}</span>
    </Tooltip>
  )

  return (
    <div className={`aiClerk-work__doc-trio${enabled ? '' : ' is-disabled'}`}>
      {wrapAction(
        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          className={`aiClerk-work__doc-trio-btn aiClerk-work__doc-trio-btn--primary${
            showStaleDot ? ' has-dot' : ''
          }`}
          onClick={onExport}
          disabled={!enabled}
        >
          下载导出
          {vipLocked && (
            <VipCrownIcon
              style={{
                width: 16,
                height: 14,
                marginLeft: 10,
                verticalAlign: 'middle'
              }}
              aria-hidden="true"
            />
          )}
        </Button>,
        enabled ? '导出当前文档版本' : disabledReason
      )}
      {wrapAction(
        <Popover
          trigger="click"
          placement="topRight"
          open={shareOpen && enabled}
          onOpenChange={(v) => {
            if (!v && shareOpen) onShareToggle()
          }}
          content={
            <div className="aiClerk-work__doc-trio-share-popover">
              <ShareTargets
                targets={DOC_TRIO_SHARE_TARGETS}
                shareUrl={shareUrl}
                emailSubject={fileName ? `分享：${fileName}` : undefined}
                title="发送给"
                description="复制链接或选一个渠道"
                onShare={onShareConsumed}
              />
            </div>
          }
        >
          <Button
            icon={<ShareAltOutlined />}
            className="aiClerk-work__doc-trio-btn aiClerk-work__doc-trio-btn--ghost aiClerk-work__doc-trio-btn--icon"
            onClick={() => {
              if (enabled) onShareToggle()
            }}
            disabled={!enabled}
            aria-label="分享"
          />
        </Popover>,
        enabled ? '分享给团队' : disabledReason
      )}
    </div>
  )
}

export default Work
